import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import ReactPlayer from 'react-player';
import { events as mockEvents, news as mockNews, locations, clubs as mockClubs } from '../data/mockData';
import api from '../api/client';
import { AdBox, WysiwygEditor, RichDisplay, countryDisplay, CountrySelect } from '../components/Shared';
import { useAuth, useData } from '../context';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';

// ── Add Event Modal ────────────────────────────────────
function AddEventModal({ onClose, onAdded, defaultType }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '', date: '', city: '', country: '', location: '', type: defaultType || 'larp',
    description: '', rules: '', youtube_url: '', max_participants: 100, image_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('image_url', r.data.url);
    } catch { setError('Nepavyko įkelti nuotraukos'); }
    e.target.value = '';
  };

  const submit = async () => {
    if (!form.name.trim() || !form.date) { setError('Pavadinimas ir data privalomi'); return; }
    if (!user) { setError('Prisijunkite norėdami pridėti renginį'); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post('/events', form);
      onAdded({ ...form, id: r.data.id, participants: 0, rating: 0, tags: [], status: r.data.status || 'pending' });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Klaida išsaugant');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'Cinzel,serif', fontSize: 18, color: 'var(--gold2)', margin: 0 }}>⚔️ {t('events.add_event')}</h2>
          <button style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text3)', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        {!user && (
          <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#ff8080', fontSize: 13 }}>
            🔐 Prisijunkite norėdami pridėti renginį
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Pavadinimas *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Renginio pavadinimas" />
          </div>
          <div className="form-group">
            <label className="form-label">Data *</label>
            <input className="form-input" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Tipas</label>
            <select className="form-input" value={form.type} onChange={e => set('type', e.target.value)}>
              {['larp','rpg','ccg','fest'].map(v => <option key={v} value={v}>{v.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Miestas</label>
            <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Vilnius" />
          </div>
          <div className="form-group">
            <label className="form-label">Šalis</label>
            <CountrySelect value={form.country} onChange={v => set('country', v)} className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Vieta</label>
            <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Adresas / pavadinimas" />
          </div>
          <div className="form-group">
            <label className="form-label">Maks. dalyvių</label>
            <input className="form-input" type="number" min="1" value={form.max_participants} onChange={e => set('max_participants', parseInt(e.target.value) || 100)} />
          </div>
          <div className="form-group">
            <label className="form-label">YouTube URL</label>
            <input className="form-input" value={form.youtube_url} onChange={e => set('youtube_url', e.target.value)} placeholder="https://youtube.com/..." />
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Paveikslėlis</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" style={{ flex: 1 }} value={form.image_url} onChange={e => set('image_url', e.target.value)} placeholder="/uploads/... arba URL" />
              <button type="button" className="btn btn-secondary" style={{ padding: '0 12px', fontSize: 16 }} onClick={() => fileRef.current.click()}>⬆</button>
            </div>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{ display: 'none' }} onChange={handleUpload} />
            {form.image_url && <img src={form.image_url} alt="" style={{ marginTop: 6, maxHeight: 60, maxWidth: '100%', borderRadius: 6, objectFit: 'cover' }} />}
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Aprašymas</label>
            <WysiwygEditor value={form.description} onChange={v => set('description', v)} placeholder="Renginio aprašymas..." minHeight={140} />
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Taisyklės</label>
            <WysiwygEditor value={form.rules} onChange={v => set('rules', v)} placeholder="Dalyvavimo taisyklės..." minHeight={100} />
          </div>
        </div>

        {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !user}>
            {saving ? '⏳ Saugoma...' : '+ Pridėti renginį'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>Renginys bus matomas po administratoriaus patvirtinimo.</div>
      </div>
    </div>
  );
}

// Fix Leaflet default marker icons broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const normEvent = e => ({
  ...e,
  youtube: e.youtube_url ?? e.youtube ?? null,
  maxParticipants: e.max_participants ?? e.maxParticipants ?? 100,
  participants: e.participants ?? 0,
  organizer: e.organizer ?? '',
  rating: e.rating ?? 0,
  tags: Array.isArray(e.tags) ? e.tags : [],
});

const normClub = c => ({
  ...c,
  youtube: c.youtube_url ?? c.youtube ?? null,
  members: c.members ?? 0,
  rating: c.rating ?? 0,
  tags: Array.isArray(c.tags) ? c.tags : [],
  image_url: c.image_url || '',
});

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// ── Shared pagination (used by Events, News, Larp) ────
function PageNav({ page, total, size, onChange }) {
  const pages = Math.ceil(total / size);
  if (pages <= 1) return null;
  const btn = (p, label, disabled) => (
    <button key={p + label} onClick={() => !disabled && onChange(p)} disabled={disabled} style={{
      minWidth: 34, height: 34, padding: '0 8px',
      background: p === page ? 'rgba(240,192,64,0.18)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${p === page ? 'rgba(240,192,64,0.45)' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 6, color: p === page ? '#f0c040' : 'rgba(200,180,255,0.65)',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13,
      fontWeight: p === page ? 700 : 400, opacity: disabled ? 0.3 : 1,
      transition: 'all .15s', fontFamily: p === page ? 'Cinzel,serif' : 'inherit',
    }}>{label}</button>
  );
  const nums = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || (i >= page - 2 && i <= page + 2)) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, margin: '36px 0 8px' }}>
      {btn(page - 1, '←', page === 1)}
      {nums.map((n, i) => n === '…'
        ? <span key={'e' + i} style={{ color: 'rgba(200,180,255,0.3)', padding: '0 4px' }}>…</span>
        : btn(n, n, false)
      )}
      {btn(page + 1, '→', page === pages)}
    </div>
  );
}

const TYPE_COLORS = { fest: '#ffa040', larp: '#ff8080', ccg: '#80b0ff', rpg: '#80d060' };
const TYPE_BG = { club: 'bg-club', larp: 'bg-larp', ccg: 'bg-ccg', rpg: 'bg-rpg', fest: 'bg-fest' };

const CLUBS_PAGE_SIZE = 18;

// ── Clubs Pagination component ─────────────────────────
function ClubsPagination({ page, total, pageSize, onPage }) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  const btn = (p, label, disabled) => (
    <button key={p + label} onClick={() => !disabled && onPage(p)} disabled={disabled} style={{
      minWidth: 34, height: 34, padding: '0 8px',
      background: p === page ? 'var(--purple)' : 'var(--bg)',
      border: `1px solid ${p === page ? 'var(--purple)' : 'var(--border2)'}`,
      borderRadius: 6, color: p === page ? '#fff' : 'var(--text2)', cursor: disabled ? 'default' : 'pointer',
      fontSize: 13, opacity: disabled ? 0.3 : 1, transition: 'all .15s',
    }}>{label}</button>
  );
  const nums = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || (i >= page - 2 && i <= page + 2)) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, margin: '28px 0 8px' }}>
      {btn(page - 1, '←', page === 1)}
      {nums.map((n, i) => n === '…'
        ? <span key={'e'+i} style={{ color: 'var(--text3)', padding: '0 4px' }}>…</span>
        : btn(n, n, false)
      )}
      {btn(page + 1, '→', page === pages)}
    </div>
  );
}

// ── Clubs List ─────────────────────────────────────────
function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function ClubsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clubs,   setClubs]   = useState(() => shuffleArr(mockClubs.map(normClub)));
  const [filter,  setFilter]  = useState('all');
  const [search,  setSearch]  = useState('');
  const [cityF,   setCityF]   = useState('');
  const [countryF,setCountryF]= useState('');
  const [page,    setPage]    = useState(1);

  useEffect(() => {
    api.get('/clubs').then(r => { if (r.data.length) setClubs(shuffleArr(r.data.map(normClub))); }).catch(() => {});
  }, []);

  // Unique cities/countries from loaded clubs
  const cities    = useMemo(() => [...new Set(clubs.map(c => c.city).filter(Boolean))].sort(), [clubs]);
  const countries = useMemo(() => [...new Set(clubs.map(c => c.country).filter(Boolean))].sort(), [clubs]);

  const filtered = useMemo(() => clubs.filter(c => {
    if (filter !== 'all' && c.type !== filter) return false;
    if (cityF    && c.city    !== cityF)    return false;
    if (countryF && c.country !== countryF) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !(c.city || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [clubs, filter, cityF, countryF, search]);

  useEffect(() => { setPage(1); }, [filter, cityF, countryF, search]);

  const pageClubs = filtered.slice((page - 1) * CLUBS_PAGE_SIZE, page * CLUBS_PAGE_SIZE);

  return (
    <div style={{ padding: '24px 20px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="section-header mb-lg">
        <h1 className="section-title"><span className="accent">✦ </span>{t('clubs.title')}</h1>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>{filtered.length} {t('clubs.title').toLowerCase()}</span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}>🔍</span>
          <input className="form-input" style={{ paddingLeft: 34 }} placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {/* City filter */}
        {cities.length > 0 && (
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }} value={cityF} onChange={e => setCityF(e.target.value)}>
            <option value="">📍 Miestas</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {/* Country filter */}
        {countries.length > 1 && (
          <select className="form-select" style={{ width: 'auto', minWidth: 110 }} value={countryF} onChange={e => setCountryF(e.target.value)}>
            <option value="">🌍 Šalis</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {/* Type filter buttons */}
        {[['all', t('common.all')], ['club', 'Club'], ['larp', 'LARP'], ['ccg', 'CCG'], ['rpg', 'RPG']].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ background: filter === v ? 'var(--purple)' : 'var(--bg)', border: `1px solid ${filter === v ? 'var(--purple)' : 'var(--border2)'}`, color: filter === v ? '#fff' : 'var(--text2)', padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', transition: 'all .2s' }}>
            {label}
          </button>
        ))}
        {/* Clear filters */}
        {(cityF || countryF || filter !== 'all' || search) && (
          <button onClick={() => { setCityF(''); setCountryF(''); setFilter('all'); setSearch(''); }} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', padding: '7px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
            ✕ {t('common.all')}
          </button>
        )}
      </div>

      {/* Grid — 3 per row, ad after each 3rd row */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16 }}>Klubų nerasta</div>
        </div>
      ) : (
        <>
          <div>
            {Array.from({ length: Math.ceil(pageClubs.length / 3) }, (_, rowIdx) => {
              const rowClubs = pageClubs.slice(rowIdx * 3, rowIdx * 3 + 3);
              return (
                <div key={rowIdx}>
                  <div className="r-grid-3" style={{ marginBottom: 16 }}>
                    {rowClubs.map(club => (
                      <div key={club.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/clubs/${club.id}`)}>
                        <div className={`card-img ${TYPE_BG[club.type] || 'bg-club'}`} style={{ position: 'relative', overflow: 'hidden' }}>
                          {club.image_url
                            ? <img src={club.image_url} alt={club.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 40 }}>{club.emoji}</span>
                          }
                          <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'rgba(90,62,140,0.85)', color: '#fff', zIndex: 1 }}>{club.type.toUpperCase()}</span>
                          <span style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#ffd060', zIndex: 1 }}>⭐ {club.rating}</span>
                          {club.status === 'pending' && <span style={{ position: 'absolute', bottom: 6, left: 8, fontSize: 10, background: '#ffa040', color: '#1a1a1a', padding: '2px 6px', borderRadius: 4, fontWeight: 700, zIndex: 1 }}>PENDING</span>}
                        </div>
                        <div className="card-body">
                          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4, lineHeight: 1.3 }}>{club.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 10, marginBottom: 8 }}>
                            <span>👥 {club.members} {t('clubs.members')}</span>
                            <span>📍 {club.city}</span>
                          </div>
                          <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{stripHtml(club.description)}</p>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {club.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(rowIdx + 1) % 3 === 0 && <div style={{ marginBottom: 20 }}><AdBox /></div>}
                </div>
              );
            })}
          </div>
          <ClubsPagination page={page} total={filtered.length} pageSize={CLUBS_PAGE_SIZE} onPage={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        </>
      )}
    </div>
  );
}

const EV_CARD = {
  larp: { bg: 'linear-gradient(160deg,#1a0000 0%,#3a0505 50%,#1a0000 100%)', accent: '#ff5050', icon: '⚔️', label: 'LARP' },
  rpg:  { bg: 'linear-gradient(160deg,#080018 0%,#1e0a40 50%,#080018 100%)', accent: '#a060ff', icon: '🎲', label: 'RPG' },
  ccg:  { bg: 'linear-gradient(160deg,#001808 0%,#063a18 50%,#001808 100%)', accent: '#40e080', icon: '🃏', label: 'CCG' },
  fest: { bg: 'linear-gradient(160deg,#180e00 0%,#3a2200 50%,#180e00 100%)', accent: '#f0c040', icon: '🏰', label: 'FEST' },
};

// ── Events List — Warhammer Community Style ────────────
const EV_PAGE_SIZE = 18;

export function EventsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState(mockEvents);
  const [showAdd, setShowAdd] = useState(false);
  const [page, setPage] = useState(1);

  const dateFilter = searchParams.get('date') || '';

  useEffect(() => {
    api.get('/events').then(r => { if (r.data.length) setEvents(r.data.map(normEvent)); }).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [filter, dateFilter]);

  const displayEvents = useMemo(() => {
    if (dateFilter) {
      const mmdd = dateFilter.slice(5); // match by MM-DD, ignore year
      return events.filter(e => e.date && e.date.slice(5) === mmdd);
    }
    return filter === 'all' ? events : events.filter(e => e.type === filter);
  }, [events, filter, dateFilter]);

  const pageEvents = useMemo(
    () => displayEvents.slice((page - 1) * EV_PAGE_SIZE, page * EV_PAGE_SIZE),
    [displayEvents, page]
  );
  const months = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];

  const filterTabs = [
    { v: 'all',  label: t('common.all'),       icon: '✦' },
    { v: 'larp', label: 'LARP',                icon: '⚔️' },
    { v: 'rpg',  label: 'RPG',                 icon: '🎲' },
    { v: 'ccg',  label: 'CCG',                 icon: '🃏' },
    { v: 'fest', label: t('events.festival'),  icon: '🏰' },
  ];

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>
      {showAdd && <AddEventModal onClose={() => setShowAdd(false)} onAdded={ev => setEvents(es => [normEvent(ev), ...es])} />}

      {/* ── WARHAMMER-STYLE HERO ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#0a0018 0%,#130828 60%,#050308 100%)', padding: '56px 24px 48px', overflow: 'hidden', borderBottom: '1px solid rgba(240,192,64,0.15)' }}>
        {/* Decorative lines */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(90deg,rgba(240,192,64,0.04) 1px,transparent 1px),linear-gradient(0deg,rgba(240,192,64,0.04) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 2, height: '100%', background: 'linear-gradient(180deg,transparent,rgba(240,192,64,0.3),transparent)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 4, padding: '3px 14px', fontSize: 11, letterSpacing: 3, color: 'rgba(240,192,64,0.7)', textTransform: 'uppercase', marginBottom: 16 }}>
            ✦ FANTASTAI.LT ✦
          </div>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(28px,5vw,52px)', fontWeight: 700, color: '#f0c040', margin: '0 0 12px', textShadow: '0 0 60px rgba(240,192,64,0.4),0 2px 4px rgba(0,0,0,0.8)', letterSpacing: 4, textTransform: 'uppercase' }}>
            {t('events.title')}
          </h1>
          <p style={{ color: 'rgba(200,180,255,0.6)', fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 28 }}>
            {t('events.motto')}
          </p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', color: 'rgba(200,200,200,0.5)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            {[['⚔️', `${events.filter(e=>e.type==='larp').length} LARP`], ['🎲', `${events.filter(e=>e.type==='rpg').length} RPG`], ['🃏', `${events.filter(e=>e.type==='ccg').length} CCG`], ['🏰', `${events.filter(e=>e.type==='fest').length} FEST`]].map(([icon, label]) => (
              <span key={label}>{icon} {label}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── FILTER BAR — WH Community style ── */}
      <div style={{ background: '#0a0810', borderBottom: '2px solid rgba(240,192,64,0.12)', position: 'sticky', top: 'var(--topbar-h)', zIndex: 50 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: '0 20px' }}>
          {filterTabs.map(({ v, label, icon }) => {
            const card = EV_CARD[v];
            const active = filter === v;
            return (
              <button key={v} onClick={() => setFilter(v)} style={{
                background: 'none', border: 'none', borderBottom: active ? `3px solid ${card?.accent || '#f0c040'}` : '3px solid transparent',
                color: active ? (card?.accent || '#f0c040') : 'rgba(200,180,255,0.5)',
                padding: '14px 20px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Cinzel,serif',
                whiteSpace: 'nowrap', transition: 'all .2s',
              }}>
                {icon} {label}
              </button>
            );
          })}
          <button onClick={() => navigate('/events/world')} style={{
            background: 'none', border: 'none', borderBottom: '3px solid transparent',
            color: 'rgba(100,180,255,0.7)',
            padding: '14px 20px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Cinzel,serif',
            whiteSpace: 'nowrap', transition: 'all .2s',
          }}>
            🌍 {t('events.world')}
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary btn-sm" style={{ margin: '8px 0', flexShrink: 0 }} onClick={() => setShowAdd(true)}>
            + {t('events.add_event')}
          </button>
        </div>
      </div>

      {/* ── SUBMENU: konkursai / balsavimai / planuoklė ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {[
            { icon: '🏆', key: 'contests', path: '/konkursai',  accent: '#f0c040' },
            { icon: '🗳️', key: 'polls',    path: '/balsavimai', accent: '#80b0ff' },
            { icon: '🗺️', key: 'travel',   path: '/planuokle',  accent: '#80d060' },
          ].map(box => (
            <div
              key={box.path}
              onClick={() => navigate(box.path)}
              style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${box.accent}33`, borderRadius: 12, padding: '18px 20px', cursor: 'pointer', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = box.accent + '88'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = box.accent + '33'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{box.icon}</span>
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 15, fontWeight: 700, color: box.accent, letterSpacing: 1 }}>{t(`events_menu.${box.key}_title`)}</span>
              </div>
              <p style={{ fontSize: 12.5, color: 'rgba(200,180,255,0.6)', lineHeight: 1.6, margin: 0 }}>{t(`events_menu.${box.key}_desc`)}</p>
              <div style={{ fontSize: 11, color: box.accent, fontWeight: 700, marginTop: 10 }}>{t('events_menu.open')} →</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── DATE FILTER INDICATOR ── */}
      {dateFilter && (
        <div style={{ background: 'rgba(240,192,64,0.06)', borderBottom: '1px solid rgba(240,192,64,0.12)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 13, color: '#f0c040', fontFamily: 'Cinzel,serif' }}>📅 {dateFilter}</span>
            <span style={{ fontSize: 12, color: 'rgba(200,180,255,0.5)' }}>— {displayEvents.length} {t('cal_page.events_count')}</span>
            <button onClick={() => setSearchParams({})} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 5, color: 'rgba(200,180,255,0.6)', padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}>× {t('common.clear')}</button>
          </div>
        </div>
      )}

      {/* ── CARDS GRID ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px' }}>
        {displayEvents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#f0c040' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>⚔️</div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 20, letterSpacing: 2, textTransform: 'uppercase' }}>{t('events.not_found')}</div>
          </div>
        ) : (
          <div>
            {Array.from({ length: Math.ceil(pageEvents.length / 3) }, (_, rowIdx) => {
              const row = pageEvents.slice(rowIdx * 3, rowIdx * 3 + 3);
              return (
                <div key={rowIdx}>
                  <div className="r-grid-3" style={{ marginBottom: 24 }}>
                    {row.map(ev => {
                      const card = EV_CARD[ev.type] || EV_CARD.fest;
                      const day = ev.date.split('-')[2];
                      const mon = months[parseInt(ev.date.split('-')[1]) - 1];
                      const fill = ev.maxParticipants > 0 ? ev.participants / ev.maxParticipants : 0;
                      return (
                        <div
                          key={ev.id}
                          onClick={() => navigate(`/events/${ev.id}`)}
                          style={{ cursor: 'pointer', background: '#0d0a18', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', transition: 'transform .18s,border-color .18s', position: 'relative' }}
                          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.borderColor=card.accent+'55'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.borderColor='rgba(255,255,255,0.06)'; }}
                        >
                          {/* Card image / hero */}
                          <div style={{ position: 'relative', height: 180, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {/* Grid texture overlay */}
                            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,0.02) 25%,transparent 25%,transparent 75%,rgba(255,255,255,0.02) 75%)', backgroundSize: '20px 20px', pointerEvents: 'none' }} />
                            <span style={{ fontSize: 64, filter: 'drop-shadow(0 0 20px ' + card.accent + '66)', zIndex: 1 }}>{card.icon}</span>
                            {/* Type badge */}
                            <div style={{ position: 'absolute', top: 12, left: 12, background: card.accent, color: '#050308', fontSize: 10, fontWeight: 800, fontFamily: 'Cinzel,serif', padding: '3px 10px', letterSpacing: 2, textTransform: 'uppercase', borderRadius: 2 }}>
                              {card.label}
                            </div>
                            {/* Date badge */}
                            <div style={{ position: 'absolute', top: 12, right: 12, textAlign: 'center', background: 'rgba(5,3,8,0.85)', border: `1px solid ${card.accent}44`, borderRadius: 4, padding: '6px 10px', minWidth: 44 }}>
                              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 22, fontWeight: 800, color: card.accent, lineHeight: 1 }}>{day}</div>
                              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', letterSpacing: 1, textTransform: 'uppercase' }}>{mon}</div>
                            </div>
                            {/* Rating */}
                            <div style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(5,3,8,0.8)', padding: '2px 8px', borderRadius: 4, fontSize: 12, color: '#ffd060', fontWeight: 600 }}>
                              ⭐ {ev.rating}
                            </div>
                            {ev.status === 'pending' && (
                              <div style={{ position: 'absolute', bottom: 12, left: 12, background: 'rgba(180,120,0,0.9)', color: '#1a1a1a', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 2, letterSpacing: 1 }}>PENDING</div>
                            )}
                          </div>

                          {/* Card body */}
                          <div style={{ padding: '16px 18px' }}>
                            <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 15, fontWeight: 700, color: '#ede8f8', marginBottom: 8, lineHeight: 1.3, letterSpacing: 0.5 }}>{ev.name}</h3>
                            <div style={{ fontSize: 12, color: 'rgba(200,180,255,0.5)', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span>📍 {ev.location}{ev.city && ev.city !== ev.location ? `, ${ev.city}` : ''}{ev.country ? ` · ${countryDisplay(ev.country, i18n.language)}` : ''}</span>
                              <span>👤 {ev.organizer}</span>
                            </div>
                            <p style={{ fontSize: 12, color: 'rgba(180,160,220,0.6)', lineHeight: 1.5, marginBottom: 12, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {stripHtml(ev.description)}
                            </p>

                            {/* Participant bar */}
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#f0c040', marginBottom: 4, letterSpacing: 1, textTransform: 'uppercase' }}>
                                <span>{t('events.participants')}</span>
                                <span style={{ color: fill > 0.9 ? '#ff5050' : card.accent }}>{ev.participants}/{ev.maxParticipants}</span>
                              </div>
                              <div style={{ height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
                                <div style={{ height: '100%', width: `${Math.min(fill * 100, 100)}%`, background: fill > 0.9 ? '#ff5050' : card.accent, borderRadius: 2, transition: 'width .5s' }} />
                              </div>
                            </div>

                            {/* Tags */}
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {ev.tags.slice(0, 3).map(tag => (
                                <span key={tag} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 2, border: `1px solid ${card.accent}33`, color: card.accent + 'aa', letterSpacing: 1, textTransform: 'uppercase' }}>{tag}</span>
                              ))}
                            </div>
                          </div>

                          {/* Bottom accent line */}
                          <div style={{ height: 2, background: `linear-gradient(90deg,${card.accent}00,${card.accent},${card.accent}00)` }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Full-width ad after every row */}
                  <div style={{ marginBottom: 28 }}>
                    <AdBox />
                  </div>
                </div>
              );
            })}
            <PageNav page={page} total={displayEvents.length} size={EV_PAGE_SIZE} onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Calendar export helpers ────────────────────────────
function _icsDate(dateStr) {
  return dateStr ? dateStr.replace(/-/g, '') : '';
}
function _icsEsc(s) {
  return (s || '').replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
}
function buildIcs(ev) {
  const pad = n => String(n).padStart(2, '0');
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth()+1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const d = _icsDate(ev.date);
  const location = `${ev.location || ''}, ${ev.city || ''}`.trim().replace(/^,\s*/, '');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Fantastai.lt//Events//LT',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:event-${ev.id}@fantastai.lt`,
    `DTSTAMP:${stamp}`,
    d ? `DTSTART;VALUE=DATE:${d}` : '',
    d ? `DTEND;VALUE=DATE:${d}` : '',
    `SUMMARY:${_icsEsc(ev.name)}`,
    `DESCRIPTION:${_icsEsc(ev.description)}`,
    location ? `LOCATION:${_icsEsc(location)}` : '',
    `URL:https://fantastai.lt/events/${ev.id}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
}
function downloadIcs(ev) {
  const blob = new Blob([buildIcs(ev)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `renginys-${ev.id}.ics`; a.click();
  URL.revokeObjectURL(url);
}
function googleCalUrl(ev) {
  const d = _icsDate(ev.date);
  const loc = `${ev.location || ''}, ${ev.city || ''}`.trim().replace(/^,\s*/, '');
  const p = new URLSearchParams({ action:'TEMPLATE', text: ev.name || '', dates: `${d}/${d}`, details: ev.description || '', location: loc });
  return `https://calendar.google.com/calendar/render?${p}`;
}
function outlookCalUrl(ev) {
  const loc = `${ev.location || ''}, ${ev.city || ''}`.trim().replace(/^,\s*/, '');
  const p = new URLSearchParams({ subject: ev.name || '', startdt: ev.date || '', enddt: ev.date || '', body: ev.description || '', location: loc, path:'/calendar/action/compose', rru:'addevent' });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p}`;
}

// ── Event Detail ───────────────────────────────────────
export function EventDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [ev, setEv] = useState(null);
  const [showReg, setShowReg] = useState(false);
  const [regForm, setRegForm] = useState({ first_name: '', last_name: '', email: '', message: '' });
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone] = useState(false);
  const [regError, setRegError] = useState('');
  const [regCaptcha, setRegCaptcha] = useState('');
  const regCaptchaResetRef = useRef(null);
  const regCaptchaOk = !TURNSTILE_SITE_KEY || !!regCaptcha;
  const [registrations, setRegistrations] = useState([]);

  const loadEvent = useCallback(() => {
    const evId = parseInt(id);
    api.get(`/events/${evId}`)
      .then(r => setEv(normEvent(r.data)))
      .catch(() => {
        const mock = mockEvents.find(e => e.id === evId);
        if (mock) setEv(normEvent(mock));
      });
  }, [id]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  useEffect(() => {
    if (!ev || !user) return;
    const isOrganizer = ev.organizer_id && user.id === ev.organizer_id;
    if (isOrganizer || isAdmin) {
      api.get(`/events/${ev.id}/registrations`).then(r => setRegistrations(r.data)).catch(() => {});
    }
  }, [ev, user, isAdmin]); // eslint-disable-line

  useEffect(() => {
    if (showReg && user) {
      const parts = (user.name || '').split(' ');
      setRegForm(f => ({
        ...f,
        first_name: f.first_name || parts[0] || '',
        last_name: f.last_name || parts.slice(1).join(' ') || '',
        email: f.email || user.email || '',
      }));
    }
  }, [showReg, user]); // eslint-disable-line

  const submitReg = async () => {
    setRegLoading(true); setRegError('');
    try {
      await api.post(`/events/${ev.id}/register`, { ...regForm, turnstile_token: regCaptcha });
      setRegDone(true);
      loadEvent();
      if (user && (isAdmin || (ev.organizer_id && user.id === ev.organizer_id))) {
        api.get(`/events/${ev.id}/registrations`).then(r => setRegistrations(r.data)).catch(() => {});
      }
    } catch (e) {
      setRegError(e.response?.data?.detail || t('events.reg_error'));
      regCaptchaResetRef.current?.();
    }
    setRegLoading(false);
  };

  if (!ev) return (
    <div style={{ background: '#050308', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#f0c040' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚔️</div>
        <div style={{ fontFamily: 'Cinzel,serif', letterSpacing: 2, textTransform: 'uppercase' }}>{t('common.loading')}</div>
      </div>
    </div>
  );

  const card = EV_CARD[ev.type] || EV_CARD.fest;
  const day = ev.date?.split('-')[2];
  const year = ev.date?.split('-')[0] || '';
  const mon = ev.date ? new Date(`${ev.date}T00:00:00`).toLocaleDateString(i18n.language, { month: 'short' }).toUpperCase() : '';
  const fill = ev.maxParticipants > 0 ? ev.participants / ev.maxParticipants : 0;

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>

      {/* ── HERO BANNER ── */}
      <div style={{ position: 'relative', height: 300, background: card.bg, overflow: 'hidden' }}>
        {/* Event image as background */}
        {ev.image_url && (
          <img src={ev.image_url} alt={ev.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
        )}
        {/* Grid texture */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,0.025) 25%,transparent 25%,transparent 75%,rgba(255,255,255,0.025) 75%)', backgroundSize: '24px 24px', zIndex: 1 }} />
        {/* Fade to page bg */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(0deg,#050308,transparent)', zIndex: 1 }} />

        {/* Large background icon */}
        <div style={{ position: 'absolute', right: '8%', top: '50%', transform: 'translateY(-50%)', fontSize: 160, opacity: 0.12, filter: `drop-shadow(0 0 40px ${card.accent})`, zIndex: 1 }}>
          {card.icon}
        </div>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          style={{ position: 'absolute', top: 20, left: 24, background: 'rgba(5,3,8,0.7)', border: `1px solid ${card.accent}44`, borderRadius: 4, color: card.accent, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontFamily: 'Cinzel,serif', letterSpacing: 1, backdropFilter: 'blur(4px)', zIndex: 2 }}
        >← {t('common.back')}</button>

        {/* Type badge */}
        <div style={{ position: 'absolute', top: 20, right: 24, background: card.accent, color: '#050308', fontSize: 11, fontWeight: 800, fontFamily: 'Cinzel,serif', padding: '4px 14px', letterSpacing: 2, borderRadius: 2, zIndex: 2 }}>
          {card.label}
        </div>

        {/* Bottom content */}
        <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24, zIndex: 2 }}>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(20px,4vw,36px)', fontWeight: 700, color: '#f0c040', margin: '0 0 10px', textShadow: `0 0 40px rgba(240,192,64,0.3),0 2px 4px rgba(0,0,0,0.9)`, lineHeight: 1.2 }}>
            {ev.name}
          </h1>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', color: 'rgba(200,180,255,0.6)', fontSize: 13, letterSpacing: 1 }}>
            <span>📅 {ev.date}</span>
            <span>📍 {ev.location}{ev.city && ev.city !== ev.location ? `, ${ev.city}` : ''}{ev.country ? ` · ${countryDisplay(ev.country, i18n.language)}` : ''}</span>
            <span>👤 {ev.organizer}</span>
            <span style={{ color: fill > 0.9 ? '#ff5050' : card.accent }}>👥 {ev.participants}/{ev.maxParticipants}</span>
            <span>⭐ {ev.rating}</span>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth: 1060, margin: '0 auto', padding: '28px 20px' }}>
        <div className="r-grid-sidebar-sm">

          {/* Left — main content */}
          <div>
            {/* Description */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg,${card.accent}44,transparent)` }} />
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 12, color: card.accent, letterSpacing: 3, textTransform: 'uppercase' }}>{t('events.description')}</span>
                <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg,transparent,${card.accent}44)` }} />
              </div>
              <RichDisplay html={ev.description} />
              {ev.image_url && (
                <div style={{ marginTop: 16, borderRadius: 6, overflow: 'hidden', border: `1px solid ${card.accent}22` }}>
                  <img src={ev.image_url} alt={ev.name} style={{ width: '100%', display: 'block', maxHeight: 400, objectFit: 'cover' }} />
                </div>
              )}
            </div>

            {/* YouTube */}
            {ev.youtube && (
              <div style={{ borderRadius: 4, overflow: 'hidden', marginBottom: 28, border: `1px solid ${card.accent}22` }}>
                <ReactPlayer url={ev.youtube} controls width="100%" height="240px" />
              </div>
            )}

            {/* Rules */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg,${card.accent}44,transparent)` }} />
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 12, color: card.accent, letterSpacing: 3, textTransform: 'uppercase' }}>{t('events.rules')}</span>
                <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg,transparent,${card.accent}44)` }} />
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${card.accent}22`, borderRadius: 4, padding: '16px 20px', color: 'rgba(200,180,255,0.6)', lineHeight: 1.9, fontSize: 14 }}>
                {ev.rules || t('events.rules_fallback')}
              </div>
            </div>

            {/* Tags */}
            {ev.tags?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 28 }}>
                {ev.tags.map(tag => (
                  <span key={tag} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 2, border: `1px solid ${card.accent}33`, color: card.accent + 'aa', letterSpacing: 1, textTransform: 'uppercase' }}>{tag}</span>
                ))}
              </div>
            )}

            {/* Ad */}
            <AdBox />
          </div>

          {/* Right — sidebar */}
          <div>
            {/* Date card */}
            <div style={{ background: card.bg, border: `1px solid ${card.accent}33`, borderRadius: 4, padding: '20px', marginBottom: 16, textAlign: 'center' }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 52, fontWeight: 800, color: card.accent, lineHeight: 1 }}>{day}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', letterSpacing: 3, textTransform: 'uppercase' }}>{mon} {year}</div>
              <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${card.accent}44,transparent)`, margin: '14px 0' }} />
              {/* Participants bar */}
              <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.5)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>{t('events.participants')}</div>
              <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginBottom: 6 }}>
                <div style={{ height: '100%', width: `${Math.min(fill * 100, 100)}%`, background: fill > 0.9 ? '#ff5050' : card.accent, borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 18, fontFamily: 'Cinzel,serif', fontWeight: 700, color: fill > 0.9 ? '#ff5050' : '#fff' }}>
                {ev.participants}<span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>/{ev.maxParticipants}</span>
              </div>
            </div>

            {/* Info */}
            <div style={{ background: '#0d0a18', border: `1px solid ${card.accent}22`, borderRadius: 4, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: card.accent, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${card.accent}22` }}>
                {t('events.info')}
              </div>
              {[
                ['loc',       '📍', t('events.location'),  `${ev.location}, ${ev.city}`],
                ['country',   '🌍', t('events.country'),   countryDisplay(ev.country, i18n.language)],
                ['organizer', '👤', t('events.organizer'), ev.organizer],
                ['rating',    '⭐', t('events.rating'),    ev.rating],
                ['type',      '🏷️', t('events.type'),      card.label],
              ].filter(([,,,val]) => val).map(([id, icon, label, val]) => (
                <div key={id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid rgba(255,255,255,0.04)`, fontSize: 13 }}>
                  <span className="lbl">{icon} {label}</span>
                  <span style={{ color: '#ede8f8', fontWeight: 500, textAlign: 'right', maxWidth: 130, fontSize: 12 }}>
                    {id === 'loc' && val && val.replace(/^,\s*/, '')
                      ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(val)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#f0c040', textDecoration: 'underline dotted' }}>{val}</a>
                      : val}
                  </span>
                </div>
              ))}
            </div>

            {/* Register button */}
            {showReg ? (
              <div style={{ background: '#0d0a18', border: `1px solid ${card.accent}44`, borderRadius: 4, padding: 18, marginBottom: 16 }}>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: card.accent, letterSpacing: 2, marginBottom: 14, textTransform: 'uppercase' }}>{t('events.registration')}</div>
                {regDone ? (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>⚔️</div>
                    <div style={{ color: card.accent, fontFamily: 'Cinzel,serif', fontSize: 14, letterSpacing: 1 }}>{t('events.reg_success')}</div>
                    <button onClick={() => setShowReg(false)} style={{ marginTop: 10, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer' }}>{t('common.close')}</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <input className="form-input" placeholder={t('events.first_name')} value={regForm.first_name}
                        onChange={e => setRegForm(f => ({...f, first_name: e.target.value}))} />
                      <input className="form-input" placeholder={t('events.last_name')} value={regForm.last_name}
                        onChange={e => setRegForm(f => ({...f, last_name: e.target.value}))} />
                    </div>
                    <input className="form-input" placeholder={t('auth.email')} value={regForm.email}
                      onChange={e => setRegForm(f => ({...f, email: e.target.value}))} style={{ marginBottom: 8 }} />
                    <textarea className="form-input" placeholder={t('events.reg_message_ph')} value={regForm.message}
                      onChange={e => setRegForm(f => ({...f, message: e.target.value}))}
                      style={{ marginBottom: 8, resize: 'vertical', minHeight: 56, fontSize: 13 }} />
                    <Turnstile onToken={setRegCaptcha} resetRef={regCaptchaResetRef} />
                    {regError && <div style={{ color: '#ff6060', fontSize: 12, marginBottom: 8 }}>⚠ {regError}</div>}
                    <button
                      style={{ width: '100%', background: `linear-gradient(135deg,${card.accent}cc,${card.accent})`, color: '#050308', border: 'none', borderRadius: 3, padding: '12px', fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', cursor: regLoading ? 'wait' : 'pointer', opacity: regLoading ? 0.7 : 1 }}
                      onClick={submitReg}
                      disabled={regLoading || !regForm.first_name.trim() || !regForm.last_name.trim() || !regForm.email.trim() || !regCaptchaOk}
                    >{regLoading ? `⏳ ${t('events.registering')}` : `${t('events.confirm')} →`}</button>
                    <button onClick={() => setShowReg(false)} style={{ width: '100%', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 12, cursor: 'pointer', marginTop: 8 }}>{t('common.cancel')}</button>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => { setShowReg(true); setRegDone(false); setRegError(''); }}
                style={{ width: '100%', background: `linear-gradient(135deg,${card.accent}bb,${card.accent})`, color: '#050308', border: 'none', borderRadius: 3, padding: '14px', fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', marginBottom: 16, boxShadow: `0 0 20px ${card.accent}44`, transition: 'all .2s' }}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform='none'}
              >
                {card.icon} {t('events.register')}
              </button>
            )}

            {/* Registrations list for organizer/admin */}
            {registrations.length > 0 && (
              <div style={{ background: '#0d0a18', border: `1px solid ${card.accent}22`, borderRadius: 4, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: card.accent, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${card.accent}22` }}>
                  👥 {t('events.registered_list')} ({registrations.length})
                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {registrations.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid rgba(255,255,255,0.04)`, fontSize: 12 }}>
                      <span style={{ color: '#ede8f8', fontWeight: 500 }}>{r.first_name} {r.last_name}</span>
                      <span style={{ color: 'rgba(200,180,255,0.45)', fontSize: 11 }}>{r.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add to calendar */}
            <div style={{ background: '#0d0a18', border: `1px solid ${card.accent}22`, borderRadius: 4, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: card.accent, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${card.accent}22` }}>
                📅 {t('events.add_to_calendar')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {/* Google Calendar */}
                <a
                  href={googleCalUrl(ev)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 4, background: 'rgba(66,133,244,0.08)', border: '1px solid rgba(66,133,244,0.22)', color: '#7aadff', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', transition: 'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(66,133,244,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(66,133,244,0.08)'}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="#7aadff" strokeWidth="1.8"/><path d="M16 2v4M8 2v4M3 9h18" stroke="#7aadff" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  Google Calendar
                </a>
                {/* Outlook */}
                <a
                  href={outlookCalUrl(ev)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 4, background: 'rgba(0,120,215,0.08)', border: '1px solid rgba(0,120,215,0.22)', color: '#60b0ff', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', transition: 'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,120,215,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(0,120,215,0.08)'}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="18" rx="2" stroke="#60b0ff" strokeWidth="1.8"/><path d="M2 8h20M8 3v5M16 3v5" stroke="#60b0ff" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  Outlook Calendar
                </a>
                {/* ICS — Apple / DAV */}
                <button
                  onClick={() => downloadIcs(ev)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 4, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(200,180,255,0.75)', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background .15s', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 3v13M8 12l4 4 4-4" stroke="rgba(200,180,255,0.75)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="rgba(200,180,255,0.75)" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  {t('events.ics_file')} · Apple / DAV
                </button>
              </div>
            </div>

            {/* Ad */}
            <AdBox />
          </div>
        </div>
      </div>

      {/* Bottom accent */}
      <div style={{ height: 2, background: `linear-gradient(90deg,transparent,${card.accent}44,transparent)`, margin: '0 40px 40px' }} />
    </div>
  );
}

// ── News Page ──────────────────────────────────────────
const NEWS_CARD = {
  Naujienos: { bg: 'linear-gradient(160deg,#00060f 0%,#001530 50%,#00060f 100%)', accent: '#4090ff', icon: '📰', label: 'NAUJIENOS' },
  RPG:       { bg: 'linear-gradient(160deg,#080018 0%,#1e0a40 50%,#080018 100%)', accent: '#a060ff', icon: '🎲', label: 'RPG' },
  LARP:      { bg: 'linear-gradient(160deg,#1a0000 0%,#3a0505 50%,#1a0000 100%)', accent: '#ff5050', icon: '⚔️', label: 'LARP' },
  CCG:       { bg: 'linear-gradient(160deg,#001808 0%,#063a18 50%,#001808 100%)', accent: '#40e080', icon: '🃏', label: 'CCG' },
};

function NewsCard({ n, large = false, onClick }) {
  const card = NEWS_CARD[n.category] || NEWS_CARD.Naujienos;
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', background: '#0d0a18', border: `1px solid ${hov ? card.accent + '55' : 'rgba(255,255,255,0.06)'}`, borderRadius: 4, overflow: 'hidden', transition: 'transform .18s,border-color .18s', transform: hov ? 'translateY(-4px)' : 'none', display: 'flex', flexDirection: 'column' }}
    >
      {/* Image area */}
      <div style={{ position: 'relative', height: large ? 240 : 160, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
        {n.image_url ? (
          <img src={n.image_url} alt={n.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,0.02) 25%,transparent 25%,transparent 75%,rgba(255,255,255,0.02) 75%)', backgroundSize: '20px 20px' }} />
            <span style={{ fontSize: large ? 72 : 52, filter: `drop-shadow(0 0 20px ${card.accent}66)`, zIndex: 1 }}>{card.icon}</span>
          </>
        )}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(0deg,#0d0a18,transparent)', pointerEvents: 'none' }} />
        {/* Category badge */}
        <div style={{ position: 'absolute', top: 12, left: 12, background: card.accent, color: '#050308', fontSize: 10, fontWeight: 800, fontFamily: 'Cinzel,serif', padding: '3px 10px', letterSpacing: 2, textTransform: 'uppercase', borderRadius: 2, zIndex: 2 }}>
          {card.label}
        </div>
        {/* Date */}
        <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(5,3,8,0.8)', border: `1px solid ${card.accent}44`, borderRadius: 4, padding: '4px 8px', fontSize: 10, color: 'rgba(255,255,255,0.55)', letterSpacing: 1, zIndex: 2 }}>
          {n.date}
        </div>
      </div>
      {/* Body */}
      <div style={{ padding: large ? '20px 22px' : '14px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: large ? 18 : 14, fontWeight: 700, color: '#ede8f8', marginBottom: 10, lineHeight: 1.35, letterSpacing: 0.3 }}>{n.title}</h3>
        <p style={{ fontSize: large ? 13 : 12, color: 'rgba(200,180,255,0.55)', lineHeight: 1.7, marginBottom: 12, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: large ? 3 : 2, WebkitBoxOrient: 'vertical' }}>
          {stripHtml(n.content)}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(Array.isArray(n.tags) ? n.tags : []).slice(0, large ? 3 : 2).map(tag => (
              <span key={tag} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 2, border: `1px solid ${card.accent}33`, color: card.accent + 'aa', letterSpacing: 1, textTransform: 'uppercase' }}>{tag}</span>
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'rgba(200,180,255,0.35)', letterSpacing: 0.5 }}>✍️ {n.author}</span>
        </div>
      </div>
      {/* Bottom accent line */}
      <div style={{ height: 2, background: `linear-gradient(90deg,${card.accent}00,${card.accent},${card.accent}00)`, flexShrink: 0 }} />
    </div>
  );
}

const NEWS_PAGE_SIZE = 18;

function CreateNewsModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const CATS = ['Naujienos','RPG','LARP','CCG','Bendruomenė'];
  const [form, setForm] = useState({ title:'', content:'', category:'Naujienos', country:'', tags:'', image_url:'' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const fileRef = useRef();
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/upload', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      set('image_url', r.data.url);
    } catch { setError('Nepavyko įkelti nuotraukos'); }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Pavadinimas privalomas'); return; }
    if (!form.content.trim()) { setError('Turinys privalomas'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/news', { ...form, tags: form.tags.split(',').map(t=>t.trim()).filter(Boolean) });
      onCreated();
      onClose();
    } catch (e) { setError(e?.response?.data?.detail || 'Klaida'); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#0d0a18',border:'1px solid rgba(64,144,255,0.25)',borderRadius:18,maxWidth:580,width:'100%',maxHeight:'90vh',overflow:'auto',padding:28 }}>
        <div style={{ fontFamily:'Cinzel,serif',fontSize:16,color:'#f0c040',marginBottom:20 }}>📰 Nauja naujiena</div>
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          <div>
            <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Pavadinimas *</label>
            <input style={{ width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none',boxSizing:'border-box' }}
              value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Naujienos pavadinimas" />
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div>
              <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Kategorija</label>
              <select style={{ width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none' }}
                value={form.category} onChange={e=>set('category',e.target.value)}>
                {CATS.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Šalis</label>
              <input style={{ width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none',boxSizing:'border-box' }}
                value={form.country} onChange={e=>set('country',e.target.value)} placeholder="LT" />
            </div>
          </div>
          <div>
            <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Žymos (per kablelį)</label>
            <input style={{ width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none',boxSizing:'border-box' }}
              value={form.tags} onChange={e=>set('tags',e.target.value)} placeholder="RPG, LARP, D&D" />
          </div>
          <div>
            <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Turinys *</label>
            <textarea style={{ width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none',boxSizing:'border-box',resize:'vertical',fontFamily:'inherit' }}
              rows={8} value={form.content} onChange={e=>set('content',e.target.value)} placeholder="Naujienos turinys..." />
          </div>
          <div>
            <label style={{ fontSize:11,color:'rgba(200,180,255,0.5)',fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:5 }}>Paveikslėlis</label>
            <div style={{ display:'flex',gap:8 }}>
              <input style={{ flex:1,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',padding:'9px 12px',fontSize:13,outline:'none' }}
                value={form.image_url} onChange={e=>set('image_url',e.target.value)} placeholder="/uploads/... arba https://..." />
              <button style={{ background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'rgba(200,180,255,0.7)',padding:'0 14px',cursor:'pointer',fontSize:13 }}
                onClick={()=>fileRef.current.click()}>⬆</button>
            </div>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{ display:'none' }} onChange={handleUpload} />
            {form.image_url && <img src={form.image_url} alt="" style={{ marginTop:8,maxHeight:80,maxWidth:'100%',borderRadius:6,objectFit:'cover' }} />}
          </div>
          {error && <div style={{ color:'#ff7070',fontSize:12 }}>⚠ {error}</div>}
          <div style={{ fontSize:11,color:'rgba(200,180,255,0.35)',padding:'10px',background:'rgba(255,255,255,0.03)',borderRadius:8 }}>
            ℹ Naujiena bus išsiųsta peržiūrai ir paskelbta po administratoriaus patvirtinimo.
          </div>
        </div>
        <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:20 }}>
          <button style={{ background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'rgba(200,180,255,0.7)',padding:'9px 20px',cursor:'pointer',fontSize:13 }} onClick={onClose}>Atšaukti</button>
          <button style={{ background:'linear-gradient(135deg,#3060c0,#5080e0)',border:'none',borderRadius:8,color:'#fff',padding:'9px 24px',cursor:'pointer',fontSize:13,fontWeight:700,opacity:saving?0.6:1 }}
            onClick={handleSubmit} disabled={saving}>{saving?'Siunčiama...':'Pateikti'}</button>
        </div>
      </div>
    </div>
  );
}

export function NewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [news, setNews] = useState(mockNews);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const loadNews = useCallback(() => {
    api.get('/news').then(r => {
      if (r.data.length) setNews(r.data.map(n => ({ ...n, tags: Array.isArray(n.tags) ? n.tags : [] })));
    }).catch(() => {});
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  useEffect(() => { setPage(1); }, [filter]);

  const filtered = filter === 'all' ? news : news.filter(n => n.category === filter);
  const pageNews  = filtered.slice((page - 1) * NEWS_PAGE_SIZE, page * NEWS_PAGE_SIZE);
  // Only page 1 shows featured (large) style
  const featured  = page === 1 ? pageNews.slice(0, 2) : [];
  const rest      = page === 1 ? pageNews.slice(2) : pageNews;

  const filterTabs = [
    { v: 'all',       label: 'VISOS',     icon: '✦' },
    { v: 'Naujienos', label: 'NAUJIENOS', icon: '📰' },
    { v: 'RPG',       label: 'RPG',       icon: '🎲' },
    { v: 'LARP',      label: 'LARP',      icon: '⚔️' },
    { v: 'CCG',       label: 'CCG',       icon: '🃏' },
  ];

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#050010 0%,#0a0820 60%,#050308 100%)', padding: '56px 24px 44px', overflow: 'hidden', borderBottom: '1px solid rgba(64,144,255,0.12)' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(64,144,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(64,144,255,0.04) 1px,transparent 1px)', backgroundSize: '70px 70px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 500, height: 300, background: 'radial-gradient(ellipse,rgba(64,90,180,0.15) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', border: '1px solid rgba(64,144,255,0.3)', borderRadius: 4, padding: '3px 14px', fontSize: 11, letterSpacing: 3, color: 'rgba(64,144,255,0.7)', textTransform: 'uppercase', marginBottom: 16 }}>✦ FANTASTAI.LT ✦</div>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(26px,5vw,50px)', fontWeight: 700, color: '#f0c040', margin: '0 0 10px', letterSpacing: 4, textTransform: 'uppercase', textShadow: '0 0 60px rgba(240,192,64,0.35)' }}>
            {t('news.title')}
          </h1>
          <p style={{ color: 'rgba(200,180,255,0.5)', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 28 }}>
            Aktualijos · Renginiai · Bendruomenė
          </p>
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', color: '#f0c040', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: user ? 20 : 0 }}>
            {Object.entries(NEWS_CARD).map(([cat, { icon, label, accent }]) => (
              <span key={cat} style={{ color: accent + '99' }}>{icon} {news.filter(n => n.category === cat).length} {label}</span>
            ))}
          </div>
          {user && (
            <button onClick={() => setCreateOpen(true)} style={{ background:'rgba(64,144,255,0.15)',border:'1px solid rgba(64,144,255,0.35)',borderRadius:10,color:'#7ab4ff',padding:'10px 24px',cursor:'pointer',fontSize:13,fontWeight:700,letterSpacing:1 }}>
              ✍️ Parašyti naujieną
            </button>
          )}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(64,144,255,0.3),transparent)' }} />
      </div>

      {createOpen && <CreateNewsModal onClose={() => setCreateOpen(false)} onCreated={loadNews} />}

      {/* ── FILTER BAR ── */}
      <div style={{ background: '#0a0810', borderBottom: '2px solid rgba(64,144,255,0.1)', position: 'sticky', top: 'var(--topbar-h)', zIndex: 50 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: '0 20px' }}>
          {filterTabs.map(({ v, label, icon }) => {
            const card = NEWS_CARD[v];
            const active = filter === v;
            const accent = card?.accent || '#f0c040';
            return (
              <button key={v} onClick={() => setFilter(v)} style={{ background: 'none', border: 'none', borderBottom: active ? `3px solid ${accent}` : '3px solid transparent', color: active ? accent : 'rgba(200,180,255,0.5)', padding: '14px 20px', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Cinzel,serif', whiteSpace: 'nowrap', transition: 'all .2s' }}>
                {icon} {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px' }}>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'rgba(200,180,255,0.35)' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>📰</div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 18, letterSpacing: 2 }}>NAUJIENŲ NERASTA</div>
          </div>
        ) : (
          <>
            {/* ── 2 FEATURED ── */}
            {featured.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: featured.length === 1 ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
                {featured.map(n => (
                  <NewsCard key={n.id} n={n} large onClick={() => navigate(`/news/${n.id}`)} />
                ))}
              </div>
            )}

            {/* ── REST in 3-col rows with ads ── */}
            {rest.length > 0 && (
              <div>
                {Array.from({ length: Math.ceil(rest.length / 3) }, (_, rowIdx) => {
                  const row = rest.slice(rowIdx * 3, rowIdx * 3 + 3);
                  return (
                    <div key={rowIdx}>
                      <div className="r-grid-3" style={{ marginBottom: 20 }}>
                        {row.map(n => (
                          <NewsCard key={n.id} n={n} onClick={() => navigate(`/news/${n.id}`)} />
                        ))}
                      </div>
                      <div style={{ marginBottom: 24 }}>
                        <AdBox />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ad after featured if no rest */}
            {rest.length === 0 && <AdBox />}
            <PageNav page={page} total={filtered.length} size={NEWS_PAGE_SIZE} onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </>
        )}
      </div>
    </div>
  );
}

// ── News Detail Page ───────────────────────────────────
const ND_NEWS_BG = {
  Naujienos: 'linear-gradient(135deg,#0e1a2c,#1a3c5c)',
  RPG:       'linear-gradient(135deg,#1a0f3c,#2d1a5c)',
  LARP:      'linear-gradient(135deg,#2c0e0e,#5c1a1a)',
  CCG:       'linear-gradient(135deg,#0e2c0e,#1a5c1a)',
};
const ND_NEWS_ICON = { Naujienos: '📰', RPG: '🎲', LARP: '⚔️', CCG: '🃏' };
const ND_EV_COLORS = { fest: '#ffa040', larp: '#ff8080', ccg: '#80b0ff', rpg: '#80d060', event: '#a080ff' };
const ND_MONTH_LT = ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'];
const ND_MONTH_SHORT = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];
const TYPE_BG_ND = { club: 'bg-club', larp: 'bg-larp', ccg: 'bg-ccg', rpg: 'bg-rpg', fest: 'bg-fest' };

function buildNdCal(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay + 6) % 7;
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  return cells;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function NewsDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { clubs: apiClubs, events: apiEvents } = useData();
  const [news, setNews] = useState(mockNews);

  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const calDays = useMemo(() => buildNdCal(calYear, calMonth), [calYear, calMonth]);
  const today = calYear === now.getFullYear() && calMonth === now.getMonth() ? now.getDate() : -1;

  const clubs  = apiClubs.length  ? apiClubs  : mockClubs;
  const events = apiEvents.length ? apiEvents : mockEvents;

  const sideEvents = useMemo(() => shuffle(events).slice(0, 3), [events]);
  const sideClubs  = useMemo(() => shuffle(clubs).slice(0, 5),  [clubs]);

  const eventDayMap = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (!ev.date) return;
      const d = new Date(ev.date);
      if (d.getMonth() === calMonth) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(ev);
      }
    });
    return map;
  }, [events, calMonth]);

  const prevMonth = () => { if (calMonth === 0) { setCalYear(y=>y-1); setCalMonth(11); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(y=>y+1); setCalMonth(0); } else setCalMonth(m=>m+1); };

  useEffect(() => {
    api.get('/news').then(r => {
      if (r.data.length) setNews(r.data.map(n => ({ ...n, tags: Array.isArray(n.tags) ? n.tags : [] })));
    }).catch(() => {});
  }, []);

  const article = news.find(n => String(n.id) === String(id));

  if (!article) return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📰</div>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 20, color: 'var(--text)', marginBottom: 8 }}>Naujiena nerasta</div>
      <button className="btn btn-outline" onClick={() => navigate('/news')}>← {t('common.back')}</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate('/news')}>
        ← {t('common.back')}
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>

        {/* ── LEFT: Article ── */}
        <div>
          {/* Hero image */}
          <div style={{ height: 320, background: ND_NEWS_BG[article.category] || ND_NEWS_BG.Naujienos, borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 72, marginBottom: 28, position: 'relative', overflow: 'hidden' }}>
            {article.image_url ? (
              <img src={article.image_url} alt={article.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ position: 'relative', zIndex: 1 }}>{ND_NEWS_ICON[article.category] || '📰'}</span>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(0,0,0,0.55) 0%,transparent 60%)', pointerEvents: 'none' }} />
            <span style={{ position: 'absolute', top: 16, left: 16, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', letterSpacing: 1, zIndex: 2 }}>
              {article.category}
            </span>
          </div>

          {/* Article body */}
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '28px 32px' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <span className="tag">{article.category}</span>
              <span style={{ fontSize: 13, color: 'var(--text3)' }}>✍️ {article.author_name || article.author}</span>
              <span style={{ fontSize: 13, color: 'var(--text3)' }}>🕒 {(article.created_at || article.date || '').slice(0,10)}</span>
              {article.source_url && (
                <a href={article.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'rgba(100,160,255,0.7)', textDecoration: 'none' }}>🔗 Šaltinis</a>
              )}
            </div>
            <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 26, fontWeight: 700, color: 'var(--text)', lineHeight: 1.3, marginBottom: 20 }}>
              {article.title}
            </h1>
            <div style={{ height: 2, background: 'linear-gradient(90deg,var(--purple),transparent)', marginBottom: 24, borderRadius: 2 }} />
            <RichDisplay html={article.content} />
            {article.tags?.length > 0 && (
              <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text3)', marginRight: 4 }}>{t('news.tags')}:</span>
                {article.tags.map(tag => <span key={tag} className="tag">#{tag}</span>)}
              </div>
            )}
          </div>

          {/* Related news */}
          <div style={{ marginTop: 32 }}>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
              ✦ {t('news.title')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14 }}>
              {news.filter(n => String(n.id) !== String(id)).slice(0, 3).map(n => (
                <div key={n.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/news/${n.id}`)}>
                  <div style={{ height: 90, background: ND_NEWS_BG[n.category] || ND_NEWS_BG.Naujienos, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, position: 'relative', overflow: 'hidden' }}>
                    {n.image_url
                      ? <img src={n.image_url} alt={n.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      : ND_NEWS_ICON[n.category] || '📰'
                    }
                  </div>
                  <div className="card-body" style={{ padding: '10px 12px' }}>
                    <span className="tag" style={{ fontSize: 10, marginBottom: 5, display: 'inline-block' }}>{n.category}</span>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.4 }}>{n.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>🕒 {(n.created_at || n.date || '').slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Sidebar ── */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 80 }}>

          {/* Mini calendar */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              📅 {t('common.calendar')}
            </div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14 }} onClick={prevMonth}>‹</button>
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: '#fff', fontWeight: 600 }}>{ND_MONTH_LT[calMonth]} {calYear}</span>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14 }} onClick={nextMonth}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'var(--border)', padding: 1 }}>
                {['P','A','T','K','Pn','Š','S'].map(d => (
                  <div key={d} style={{ background: 'var(--bg2)', textAlign: 'center', fontSize: 9, fontWeight: 600, color: 'var(--text3)', padding: '4px 0' }}>{d}</div>
                ))}
                {calDays.map((day, i) => {
                  const hasEv = day && eventDayMap[day];
                  const isToday = day === today;
                  const dateStr = day ? `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : '';
                  return (
                    <div key={i}
                      onClick={() => hasEv && navigate(`/events?date=${dateStr}`)}
                      style={{ background: isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)', textAlign: 'center', padding: '4px 2px', fontSize: 10, cursor: hasEv ? 'pointer' : 'default', position: 'relative', minHeight: 22, color: !day ? 'transparent' : isToday ? 'var(--gold2)' : 'var(--text)', fontWeight: isToday ? 700 : 400 }}
                      onMouseEnter={e => { if (hasEv) e.currentTarget.style.background = 'rgba(240,192,64,0.15)'; }}
                      onMouseLeave={e => { if (hasEv) e.currentTarget.style.background = isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)'; }}
                    >
                      {day}
                      {hasEv && <span style={{ position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)', width: 3, height: 3, borderRadius: '50%', background: 'var(--gold2)', display: 'block' }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Random 3 events */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              ⚔️ {t('events.upcoming')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sideEvents.map(ev => {
                const color = ND_EV_COLORS[ev.type] || '#a080ff';
                const parts = (ev.date || '').split('-');
                return (
                  <div key={ev.id} onClick={() => navigate(`/events/${ev.id}`)}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', display: 'flex', gap: 0 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = color}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    {/* Date strip */}
                    <div style={{ background: color + '22', borderRight: `2px solid ${color}`, padding: '8px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 40 }}>
                      <span style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 700, color, lineHeight: 1 }}>{parts[2]}</span>
                      <span style={{ fontSize: 8, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>{ND_MONTH_SHORT[parseInt(parts[1])-1]}</span>
                    </div>
                    <div style={{ padding: '8px 10px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>📍 {ev.city}</div>
                    </div>
                  </div>
                );
              })}
              <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '6px 0', fontSize: 11 }} onClick={() => navigate('/events')}>
                {t('common.all_events')}
              </button>
            </div>
          </div>

          {/* Random 5 clubs */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              🏰 {t('clubs.featured')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sideClubs.map(club => (
                <div key={club.id} onClick={() => navigate(`/clubs/${club.id}`)}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--purple2)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div className={`${TYPE_BG_ND[club.type] || 'bg-club'}`} style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                    {club.image_url
                      ? <img src={club.image_url} alt={club.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      : club.emoji
                    }
                  </div>
                  <div style={{ padding: '7px 10px', flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{club.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>📍 {club.city} · {(club.type || '').toUpperCase()}</div>
                  </div>
                </div>
              ))}
              <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '6px 0', fontSize: 11 }} onClick={() => navigate('/clubs')}>
                {t('common.all_clubs')}
              </button>
            </div>
          </div>

        </aside>
      </div>
    </div>
  );
}

// ── Calendar Page ──────────────────────────────────────
const CAL_EV_PAGE = 12;

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clubs: allClubs } = useData();

  const todayDate = new Date();
  const [year,      setYear]      = useState(todayDate.getFullYear());
  const [month,     setMonth]     = useState(todayDate.getMonth()); // 0-indexed
  const [events,    setEvents]    = useState(mockEvents);
  const [calEvPage, setCalEvPage] = useState(1);

  const monthNames = ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'];
  const weekDays   = ['Pir','Ant','Tre','Ket','Pen','Šeš','Sek'];

  // Correct days in month + Monday-based first day offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow    = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDow }, () => null);

  const prevMonth = () => { setCalEvPage(1); if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { setCalEvPage(1); if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  useEffect(() => {
    api.get('/events').then(r => { if (r.data.length) setEvents(r.data.map(normEvent)); }).catch(() => {});
  }, []);

  // Group events by day (match by month only — works with both mock and real data)
  const eventDays = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (!ev.date) return;
      const d = new Date(ev.date);
      if (d.getMonth() !== month) return;
      const day = d.getDate();
      if (!map[day]) map[day] = [];
      map[day].push(ev);
    });
    return map;
  }, [events, month]);

  // Flat sorted list of events for the currently viewed month (calendar grid)
  const monthEvents = useMemo(() => {
    return events
      .filter(ev => {
        if (!ev.date) return false;
        const d = new Date(ev.date);
        return d.getMonth() === month && d.getFullYear() === year;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events, month, year]);

  // Always next calendar month from today (for the events list sections)
  const nextMonthDate = useMemo(() => new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 1), []); // eslint-disable-line
  const nextMonthEvents = useMemo(() => {
    const nm = nextMonthDate.getMonth();
    const ny = nextMonthDate.getFullYear();
    return events
      .filter(ev => {
        if (!ev.date) return false;
        const d = new Date(ev.date);
        return d.getMonth() === nm && d.getFullYear() === ny;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events, nextMonthDate]);

  const calPageEvents = useMemo(
    () => nextMonthEvents.slice((calEvPage - 1) * CAL_EV_PAGE, calEvPage * CAL_EV_PAGE),
    [nextMonthEvents, calEvPage]
  );

  // 3 random featured clubs — chosen once when clubs load
  const featuredClubs = useMemo(() => {
    const src = allClubs.length ? allClubs : mockClubs;
    return [...src].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [allClubs.length]); // eslint-disable-line

  const today = todayDate.getDate();

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#0a0018 0%,#130828 60%,#050308 100%)', padding: '44px 24px 36px', borderBottom: '1px solid rgba(240,192,64,0.12)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(240,192,64,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(240,192,64,0.04) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 4, padding: '3px 14px', fontSize: 11, letterSpacing: 3, color: 'rgba(240,192,64,0.7)', textTransform: 'uppercase', marginBottom: 14 }}>✦ FANTASTAI.LT ✦</div>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(24px,4vw,44px)', fontWeight: 700, color: '#f0c040', margin: '0 0 8px', letterSpacing: 4, textTransform: 'uppercase', textShadow: '0 0 40px rgba(240,192,64,0.3)' }}>
            {t('nav.calendar')}
          </h1>
          <p style={{ color: 'rgba(200,180,255,0.5)', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase' }}>
            {events.length} renginiai · {monthNames[month]} {year}
          </p>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px' }}>
        <div className="r-grid-sidebar">

          {/* Left — calendar */}
          <div>
            <div style={{ background: '#0d0a18', border: '1px solid rgba(240,192,64,0.1)', borderRadius: 4, overflow: 'hidden', marginBottom: 24 }}>
              {/* Month header */}
              <div style={{ background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(240,192,64,0.15)' }}>
                <button onClick={prevMonth} style={{ background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 4, color: '#f0c040', width: 36, height: 36, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 20, color: '#f0c040', fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase' }}>{monthNames[month]} {year}</span>
                <button onClick={nextMonth} style={{ background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 4, color: '#f0c040', width: 36, height: 36, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
              </div>

              {/* Weekday headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: '#080610' }}>
                {weekDays.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(200,180,255,0.35)', padding: '10px 0', letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Cinzel,serif' }}>{d}</div>
                ))}
              </div>

              {/* Days grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'rgba(255,255,255,0.03)', padding: 1 }}>
                {[...blanks, ...days].map((day, i) => {
                  const evs  = day ? (eventDays[day] || []) : [];
                  const ev   = evs[0] || null;
                  const card = ev ? (EV_CARD[ev.type] || EV_CARD.fest) : null;
                  const isToday = day === today && month === todayDate.getMonth() && year === todayDate.getFullYear();
                  const hasEv  = evs.length > 0;
                  const dateStr = day ? `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : '';
                  return (
                    <div
                      key={i}
                      onClick={() => hasEv && navigate(`/events?date=${dateStr}`)}
                      style={{
                        background: hasEv ? `${card.accent}10` : '#0d0a18',
                        textAlign: 'center', padding: '8px 4px', fontSize: 14,
                        cursor: hasEv ? 'pointer' : 'default',
                        color: !day ? 'transparent' : isToday ? '#f0c040' : hasEv ? card.accent : 'rgba(200,180,255,0.5)',
                        fontWeight: hasEv || isToday ? 700 : 400,
                        position: 'relative', minHeight: 60,
                        border: isToday ? '1px solid rgba(240,192,64,0.5)' : hasEv ? `1px solid ${card.accent}33` : '1px solid transparent',
                        borderRadius: 2, transition: 'all .15s',
                        fontFamily: isToday || hasEv ? 'Cinzel,serif' : 'inherit',
                      }}
                      onMouseEnter={e => { if (hasEv) e.currentTarget.style.background = card.accent + '25'; }}
                      onMouseLeave={e => { if (hasEv) e.currentTarget.style.background = card.accent + '10'; }}
                    >
                      {day || ''}
                      {hasEv && (
                        <>
                          <div style={{ fontSize: 14, marginTop: 2 }}>{card.icon}</div>
                          <div style={{ fontSize: 8, color: card.accent, marginTop: 1, lineHeight: 1.2, padding: '0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: 0.5 }}>
                            {evs.length > 1 ? `${evs.length} renginiai` : ev.name.split(' ').slice(0,2).join(' ')}
                          </div>
                          {/* dot */}
                          <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: evs.length > 1 ? 20 : 5, height: 5, borderRadius: 3, background: card.accent, opacity: 0.8 }} />
                        </>
                      )}
                      {isToday && !hasEv && (
                        <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: '#f0c040' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
              {Object.entries(EV_CARD).map(([type, { accent, icon, label }]) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(200,180,255,0.5)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: accent }} />
                  {icon} {label}
                </div>
              ))}
            </div>

            {/* Ad below calendar */}
            <AdBox position="calendar" />

            {/* ── Kito mėnesio renginiai (1×2, 12/page) ── */}
            <div style={{ marginTop: 28 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: '#f0c040', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid rgba(240,192,64,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>✦ {t('cal_page.next_month_events')}</span>
                <span style={{ fontSize: 10, color: 'rgba(200,180,255,0.4)', letterSpacing: 1 }}>{nextMonthEvents.length} {t('cal_page.events_count')}</span>
              </div>

              {nextMonthEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(200,180,255,0.25)' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
                  <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, letterSpacing: 2 }}>RENGINIŲ NĖRA</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
                    {calPageEvents.map(ev => {
                      const card = EV_CARD[ev.type] || EV_CARD.fest;
                      const day  = ev.date?.split('-')[2];
                      const mon  = ev.date ? (['Sau','Vas','Kov','Bal','Geg','Bir','Lie','Rug','Rgs','Spa','Lap','Grd'])[parseInt(ev.date.split('-')[1]) - 1] : '';
                      return (
                        <div
                          key={ev.id}
                          onClick={() => navigate(`/events?date=${ev.date ? ev.date.slice(0,10) : ''}`)}
                          style={{ background: '#0d0a18', border: `1px solid ${card.accent}22`, borderLeft: `3px solid ${card.accent}`, borderRadius: 6, padding: '12px 13px', cursor: 'pointer', transition: 'all .15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = card.accent + '10'; e.currentTarget.style.borderColor = card.accent + '44'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#0d0a18'; e.currentTarget.style.borderColor = card.accent + '22'; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7 }}>
                            <div style={{ background: 'rgba(5,3,8,0.8)', border: `1px solid ${card.accent}44`, borderRadius: 4, padding: '4px 7px', textAlign: 'center', flexShrink: 0 }}>
                              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 800, color: card.accent, lineHeight: 1 }}>{day}</div>
                              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.45)', letterSpacing: 1 }}>{mon}</div>
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, fontWeight: 700, color: '#ede8f8', lineHeight: 1.3, marginBottom: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ev.name}</div>
                              <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.4)' }}>📍 {ev.city}{ev.country ? ` · ${countryDisplay(ev.country, i18n.language)}` : ''}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 2, background: card.accent + '20', color: card.accent, fontWeight: 700, letterSpacing: 1 }}>{card.label}</span>
                            <span style={{ fontSize: 9, color: 'rgba(200,180,255,0.3)' }}>👥 {ev.participants}/{ev.maxParticipants}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <PageNav page={calEvPage} total={nextMonthEvents.length} size={CAL_EV_PAGE} onChange={p => { setCalEvPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                </>
              )}
            </div>
          </div>

          {/* Right — sidebar */}
          <div>
            {/* ── Featured Clubs ── */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: '#f0c040', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(240,192,64,0.15)', display: 'flex', alignItems: 'center', gap: 8 }}>
                ✦ Išskirtiniai klubai
              </div>
              {featuredClubs.map(club => (
                <div
                  key={club.id}
                  onClick={() => navigate(`/clubs/${club.id}`)}
                  style={{ background: '#0d0a18', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 12 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(240,192,64,0.3)'; e.currentTarget.style.background = 'rgba(240,192,64,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = '#0d0a18'; }}
                >
                  <div style={{ fontSize: 28, flexShrink: 0 }}>{club.emoji || '🐉'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: '#ede8f8', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{club.name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.45)' }}>📍 {club.city || '—'}{club.country ? ` · ${countryDisplay(club.country, i18n.language)}` : ''}</div>
                  </div>
                  <span style={{ fontSize: 9, color: '#f0c040', flexShrink: 0 }}>→</span>
                </div>
              ))}
            </div>

            {/* ── Next month events sidebar ── */}
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, color: '#f0c040', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid rgba(240,192,64,0.15)' }}>
              {t('cal_page.next_month_events')}
            </div>

            {nextMonthEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(200,180,255,0.3)' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
                <div style={{ fontSize: 12, letterSpacing: 1 }}>{t('cal_page.no_events')}</div>
              </div>
            ) : (
              nextMonthEvents.slice(0, 5).map((ev, i) => {
                const card = EV_CARD[ev.type] || EV_CARD.fest;
                return (
                  <div key={ev.id}>
                    <div
                      onClick={() => navigate(`/events/${ev.id}`)}
                      style={{ background: '#0d0a18', border: `1px solid ${card.accent}22`, borderRadius: 4, padding: '12px 14px', marginBottom: 10, cursor: 'pointer', transition: 'all .15s', borderLeft: `3px solid ${card.accent}` }}
                      onMouseEnter={e => { e.currentTarget.style.background = card.accent + '10'; e.currentTarget.style.borderColor = card.accent + '55'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#0d0a18'; e.currentTarget.style.borderColor = card.accent + '22'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        <span style={{ fontSize: 16 }}>{card.icon}</span>
                        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: '#ede8f8', lineHeight: 1.3, flex: 1 }}>{ev.name}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.45)', letterSpacing: 0.5 }}>📅 {ev.date} · 📍 {ev.city}{ev.country ? ` · ${countryDisplay(ev.country, i18n.language)}` : ''}</div>
                      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 2, background: card.accent + '20', color: card.accent, fontWeight: 700, letterSpacing: 1 }}>{card.label}</span>
                        <span style={{ fontSize: 10, color: 'rgba(200,180,255,0.3)' }}>👥 {ev.participants}/{ev.maxParticipants}</span>
                      </div>
                    </div>
                    {/* Ad after every 3rd event */}
                    {(i + 1) % 3 === 0 && <div style={{ marginBottom: 10 }}><AdBox /></div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Map fly-to controller ──────────────────────────────
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 14, { duration: 1 });
  }, [target, map]);
  return null;
}

const LOC_ICON = { shop: '🏪', outdoor: '🌲', venue: '🏰', club: '🏰', larp: '⚔️', event: '📅' };
const COUNTRY_FLAG = { LT: '🇱🇹', UK: '🇬🇧', DE: '🇩🇪', CH: '🇨🇭', BE: '🇧🇪', SI: '🇸🇮', PT: '🇵🇹', FR: '🇫🇷', PL: '🇵🇱', LV: '🇱🇻', EE: '🇪🇪' };

const LOC_TYPES = [
  { v: '',        label: 'Visi' },
  { v: 'shop',    label: '🏪 Shop' },
  { v: 'club',    label: '🏰 Club' },
  { v: 'larp',    label: '⚔️ LARP' },
  { v: 'event',   label: '📅 Event' },
  { v: 'venue',   label: '🏛️ Venue' },
  { v: 'outdoor', label: '🌲 Outdoor' },
];

const LOC_PAGE = 16;

// ── Locations Page ─────────────────────────────────────
export function LocationsPage() {
  const { t } = useTranslation();
  const [locs,      setLocs]      = useState(locations);
  const [active,    setActive]    = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [search,    setSearch]    = useState('');
  const [typeF,     setTypeF]     = useState('');
  const [countryF,  setCountryF]  = useState('');
  const [cityF,     setCityF]     = useState('');
  const [page,      setPage]      = useState(1);

  useEffect(() => {
    api.get('/locations').then(r => { if (r.data?.length) setLocs(r.data); }).catch(() => {});
  }, []);

  const countries = useMemo(() => [...new Set(locs.map(l => l.country).filter(Boolean))].sort(), [locs]);
  const cities    = useMemo(() => {
    const src = countryF ? locs.filter(l => l.country === countryF) : locs;
    return [...new Set(src.map(l => l.city).filter(Boolean))].sort();
  }, [locs, countryF]);

  const filtered = useMemo(() => locs.filter(l => {
    if (typeF    && l.type    !== typeF)    return false;
    if (countryF && l.country !== countryF) return false;
    if (cityF    && l.city    !== cityF)    return false;
    if (search) {
      const q = search.toLowerCase();
      if (!l.name.toLowerCase().includes(q) &&
          !(l.address || '').toLowerCase().includes(q) &&
          !(l.city    || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [locs, typeF, countryF, cityF, search]);

  useEffect(() => { setPage(1); }, [typeF, countryF, cityF, search]);

  const totalPages = Math.ceil(filtered.length / LOC_PAGE) || 1;
  const pageLocs   = filtered.slice((page - 1) * LOC_PAGE, page * LOC_PAGE);

  const handleCardClick = (loc) => { setActive(loc.id); setFlyTarget(loc); };

  const pill = (active) => ({
    padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
    fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap',
    border: `1px solid ${active ? 'rgba(240,192,64,0.6)' : 'rgba(255,255,255,0.1)'}`,
    background: active ? 'rgba(240,192,64,0.18)' : 'rgba(255,255,255,0.05)',
    color: active ? '#f0c040' : 'rgba(200,180,240,0.75)',
    transition: 'all .15s',
  });

  return (
    <div className="loc-map-wrap" style={{ position: 'relative', height: 'calc(100vh - var(--topbar-h))', overflow: 'hidden' }}>

      {/* Full-screen map — show all filtered markers */}
      <MapContainer
        center={[51.5, 10.0]}
        zoom={4}
        style={{ position: 'absolute', inset: 0, height: '100%', width: '100%', zIndex: 0 }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <FlyTo target={flyTarget} />
        {filtered.map(loc => (
          <Marker key={loc.id} position={[loc.lat, loc.lng]}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <strong style={{ fontFamily: 'Cinzel,serif', fontSize: 14 }}>{loc.name}</strong>
                {loc.country && COUNTRY_FLAG[loc.country] ? ` ${COUNTRY_FLAG[loc.country]}` : ''}
                <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>📍 {loc.address}</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>⭐ {loc.rating} · 🕒 {loc.hours}</div>
                <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>{loc.description}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Floating left panel */}
      <div className="loc-panel" style={{
        background: 'rgba(14,12,22,0.93)',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid rgba(90,62,140,0.4)',
        display: 'flex', flexDirection: 'column',
        boxShadow: '4px 0 32px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(90,62,140,0.3)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: '#f0c040', margin: 0, letterSpacing: 1 }}>
              📍 {t('map.title')}
            </h2>
            <span style={{ fontSize: 11, color: 'rgba(180,160,220,0.55)' }}>{filtered.length} {t('map.locations_count')}</span>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'rgba(180,160,220,0.5)' }}>🔍</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('map.find')}
              style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '7px 10px 7px 30px', color: '#f0eeff', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Type filter chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {LOC_TYPES.map(({ v, label }) => (
              <button key={v} onClick={() => setTypeF(v)} style={pill(typeF === v)}>{label}</button>
            ))}
          </div>

          {/* Country + City dropdowns */}
          <div style={{ display: 'flex', gap: 6 }}>
            {countries.length > 1 && (
              <select value={countryF} onChange={e => { setCountryF(e.target.value); setCityF(''); }} style={{ flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, padding: '6px 8px', color: 'rgba(210,195,250,0.85)', fontSize: 11, outline: 'none' }}>
                <option value="">🌍 Šalis</option>
                {countries.map(c => <option key={c} value={c}>{COUNTRY_FLAG[c] || ''} {c}</option>)}
              </select>
            )}
            {cities.length > 1 && (
              <select value={cityF} onChange={e => setCityF(e.target.value)} style={{ flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, padding: '6px 8px', color: 'rgba(210,195,250,0.85)', fontSize: 11, outline: 'none' }}>
                <option value="">📍 Miestas</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {(typeF || countryF || cityF || search) && (
              <button onClick={() => { setTypeF(''); setCountryF(''); setCityF(''); setSearch(''); }} style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.25)', borderRadius: 7, padding: '6px 9px', color: 'rgba(255,140,140,0.8)', fontSize: 11, cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* Cards list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 0' }}>
          {pageLocs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(180,160,220,0.4)', fontSize: 13 }}>Nerasta</div>
          )}
          {pageLocs.map(loc => (
            <div
              key={loc.id}
              onClick={() => handleCardClick(loc)}
              style={{
                background: active === loc.id ? 'rgba(90,62,140,0.25)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${active === loc.id ? 'rgba(90,62,140,0.7)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 10, padding: '11px 12px', marginBottom: 7,
                cursor: 'pointer', transition: 'background .15s, border .15s',
                boxShadow: active === loc.id ? '0 0 16px rgba(90,62,140,0.3)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{LOC_ICON[loc.type] || '📍'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 2 }}>
                    <span style={{ fontFamily: 'Cinzel,serif', fontSize: 12, fontWeight: 600, color: '#f0f0ff' }}>{loc.name}</span>
                    {loc.country && COUNTRY_FLAG[loc.country] && <span style={{ fontSize: 12 }}>{COUNTRY_FLAG[loc.country]}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(180,160,220,0.7)', marginBottom: 3 }}>📍 {loc.city}{loc.city && loc.address ? ' · ' : ''}{loc.address}</div>
                  <div style={{ fontSize: 11, color: 'rgba(210,210,230,0.6)', lineHeight: 1.4, marginBottom: 5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{loc.description}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(160,140,200,0.55)' }}>
                    <span>🕒 {loc.hours}</span>
                    <span style={{ color: '#ffd060', fontWeight: 600 }}>⭐ {loc.rating}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ flexShrink: 0, borderTop: '1px solid rgba(90,62,140,0.25)', padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '5px 12px', color: page <= 1 ? 'rgba(255,255,255,0.2)' : 'rgba(210,195,250,0.8)', cursor: page <= 1 ? 'default' : 'pointer', fontSize: 14 }}
            >←</button>
            <span style={{ fontSize: 11, color: 'rgba(180,160,220,0.55)' }}>
              {page} / {totalPages} · {filtered.length} {t('map.locations_count')}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '5px 12px', color: page >= totalPages ? 'rgba(255,255,255,0.2)' : 'rgba(210,195,250,0.8)', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 14 }}
            >→</button>
          </div>
        )}
      </div>
    </div>
  );
}

const LARP_PAGE_SIZE = 18;

// ── LARP Page ──────────────────────────────────────────
export function LarpPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState(mockEvents);
  const [clubs, setClubs] = useState(mockClubs);
  const [showAdd, setShowAdd] = useState(false);
  const [clubPage, setClubPage] = useState(1);
  const [evPage,   setEvPage]   = useState(1);

  useEffect(() => {
    api.get('/events').then(r => { if (r.data.length) setEvents(r.data.map(normEvent)); }).catch(() => {});
    api.get('/clubs').then(r => { if (r.data.length) setClubs(r.data.map(normClub)); }).catch(() => {});
  }, []);

  const larpEvents    = events.filter(e => e.type === 'larp');
  const larpClubs     = clubs.filter(c => c.type === 'larp' || c.tags?.includes('LARP'));
  const pageClubs     = larpClubs.slice((clubPage - 1) * LARP_PAGE_SIZE, clubPage * LARP_PAGE_SIZE);
  const pageLarpEvs   = larpEvents.slice((evPage - 1) * LARP_PAGE_SIZE, evPage * LARP_PAGE_SIZE);
  const months = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];
  const ACCENT = '#ff5050';

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>
      {showAdd && <AddEventModal defaultType="larp" onClose={() => setShowAdd(false)} onAdded={ev => setEvents(es => [normEvent(ev), ...es])} />}

      {/* ── HERO ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#1a0000 0%,#2d0505 40%,#050308 100%)', padding: '64px 24px 52px', overflow: 'hidden', borderBottom: `1px solid ${ACCENT}22` }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${ACCENT}06 1px,transparent 1px),linear-gradient(90deg,${ACCENT}06 1px,transparent 1px)`, backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 500, height: 300, background: `radial-gradient(ellipse,${ACCENT}14 0%,transparent 70%)`, pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', border: `1px solid ${ACCENT}44`, borderRadius: 4, padding: '3px 14px', fontSize: 11, letterSpacing: 3, color: ACCENT + 'aa', textTransform: 'uppercase', marginBottom: 16 }}>✦ FANTASTAI.LT ✦</div>
          <div style={{ fontSize: 72, marginBottom: 12, filter: `drop-shadow(0 0 30px ${ACCENT}66)` }}>⚔️</div>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(28px,5vw,52px)', fontWeight: 700, color: ACCENT, margin: '0 0 12px', letterSpacing: 4, textTransform: 'uppercase', textShadow: `0 0 60px ${ACCENT}44` }}>
            LARP LIETUVA
          </h1>
          <p style={{ color: 'rgba(255,180,180,0.55)', fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 28 }}>
            {t('larp.description')}
          </p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', color: 'rgba(255,160,160,0.45)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 28 }}>
            <span>⚔️ {larpEvents.length} RENGINIAI</span>
            <span>🛡️ {larpClubs.length} KLUBAI</span>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            style={{ background: `linear-gradient(135deg,${ACCENT}cc,${ACCENT})`, color: '#050308', border: 'none', borderRadius: 3, padding: '13px 32px', fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', boxShadow: `0 0 28px ${ACCENT}44` }}
          >+ {t('events.add_event')}</button>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', height: 1, background: `linear-gradient(90deg,transparent,${ACCENT}44,transparent)` }} />
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '36px 20px' }}>

        {/* ── LARP PROJEKTAI (clubs) ── */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ height: 1, background: `linear-gradient(90deg,${ACCENT}44,transparent)`, flex: 1 }} />
            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: ACCENT, letterSpacing: 3, textTransform: 'uppercase' }}>⚔️ LARP Projektai</span>
            <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${ACCENT}44)`, flex: 1 }} />
          </div>

          {larpClubs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,160,160,0.3)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🛡️</div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, letterSpacing: 2 }}>PROJEKTŲ NĖRA</div>
            </div>
          ) : (
            <div className="r-grid-3">
              {pageClubs.map(club => (
                <div
                  key={club.id}
                  onClick={() => navigate(`/clubs/${club.id}`)}
                  style={{ cursor: 'pointer', background: '#0d0a18', border: `1px solid rgba(255,80,80,0.1)`, borderRadius: 4, overflow: 'hidden', transition: 'all .18s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.borderColor=ACCENT+'44'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.borderColor='rgba(255,80,80,0.1)'; }}
                >
                  <div style={{ height: 140, background: 'linear-gradient(160deg,#1a0000,#3a0505)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,0.02) 25%,transparent 25%,transparent 75%,rgba(255,255,255,0.02) 75%)', backgroundSize: '18px 18px' }} />
                    {club.image_url
                      ? <img src={club.image_url} alt={club.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                      : <span style={{ fontSize: 52, filter: `drop-shadow(0 0 16px ${ACCENT}66)`, zIndex: 1 }}>{club.emoji}</span>
                    }
                    {club.rating >= 4.9 && (
                      <div style={{ position: 'absolute', top: 10, right: 10, background: '#f0c040', color: '#050308', fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 2, letterSpacing: 1, zIndex: 2 }}>TOP</div>
                    )}
                  </div>
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700, color: '#ede8f8', marginBottom: 6, lineHeight: 1.3 }}>{club.name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,160,160,0.5)', marginBottom: 10 }}>📍 {club.city} · ⭐ {club.rating} · 👥 {club.members}</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {club.tags.slice(0, 3).map(tag => (
                        <span key={tag} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 2, border: `1px solid ${ACCENT}33`, color: ACCENT + 'aa', letterSpacing: 1, textTransform: 'uppercase' }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ height: 2, background: `linear-gradient(90deg,${ACCENT}00,${ACCENT},${ACCENT}00)` }} />
                </div>
              ))}
            </div>
          )}
          <PageNav page={clubPage} total={larpClubs.length} size={LARP_PAGE_SIZE} onChange={p => { setClubPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          <div style={{ marginTop: 20 }}><AdBox /></div>
        </div>

        {/* ── LARP RENGINIAI ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ height: 1, background: `linear-gradient(90deg,${ACCENT}44,transparent)`, flex: 1 }} />
            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: ACCENT, letterSpacing: 3, textTransform: 'uppercase' }}>⚔️ LARP Renginiai</span>
            <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${ACCENT}44)`, flex: 1 }} />
          </div>

          {larpEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,160,160,0.3)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>⚔️</div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, letterSpacing: 2 }}>RENGINIŲ NĖRA</div>
            </div>
          ) : (
            <div className="r-grid-3">
              {pageLarpEvs.map(ev => {
                const day = ev.date?.split('-')[2];
                const mon = ev.date ? months[parseInt(ev.date.split('-')[1]) - 1] : '';
                const fill = ev.maxParticipants > 0 ? ev.participants / ev.maxParticipants : 0;
                return (
                  <div
                    key={ev.id}
                    onClick={() => navigate(`/events/${ev.id}`)}
                    style={{ cursor: 'pointer', background: '#0d0a18', border: `1px solid rgba(255,80,80,0.1)`, borderRadius: 4, overflow: 'hidden', transition: 'all .18s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.borderColor=ACCENT+'44'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.borderColor='rgba(255,80,80,0.1)'; }}
                  >
                    <div style={{ position: 'relative', height: 160, background: 'linear-gradient(160deg,#1a0000,#3a0505)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,0.02) 25%,transparent 25%,transparent 75%,rgba(255,255,255,0.02) 75%)', backgroundSize: '20px 20px' }} />
                      {ev.image_url
                        ? <img src={ev.image_url} alt={ev.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                        : <span style={{ fontSize: 56, filter: `drop-shadow(0 0 20px ${ACCENT}66)`, zIndex: 1 }}>⚔️</span>
                      }
                      <div style={{ position: 'absolute', top: 10, left: 10, background: ACCENT, color: '#050308', fontSize: 9, fontWeight: 800, fontFamily: 'Cinzel,serif', padding: '2px 8px', letterSpacing: 2, borderRadius: 2, zIndex: 2 }}>LARP</div>
                      <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(5,3,8,0.85)', border: `1px solid ${ACCENT}44`, borderRadius: 4, padding: '4px 8px', textAlign: 'center', minWidth: 38, zIndex: 2 }}>
                        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 18, fontWeight: 800, color: ACCENT, lineHeight: 1 }}>{day}</div>
                        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.5)', letterSpacing: 1 }}>{mon}</div>
                      </div>
                      <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(5,3,8,0.8)', padding: '2px 7px', borderRadius: 4, fontSize: 11, color: '#ffd060', zIndex: 2 }}>⭐ {ev.rating}</div>
                      {ev.status === 'pending' && <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(180,120,0,0.9)', color: '#1a1a1a', fontSize: 8, fontWeight: 800, padding: '2px 7px', borderRadius: 2, letterSpacing: 1, zIndex: 2 }}>PENDING</div>}
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700, color: '#ede8f8', marginBottom: 7, lineHeight: 1.3 }}>{ev.name}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,160,160,0.45)', marginBottom: 10 }}>📍 {ev.location}, {ev.city}</div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(255,160,160,0.4)', marginBottom: 4, letterSpacing: 1, textTransform: 'uppercase' }}>
                          <span>Dalyviai</span>
                          <span style={{ color: fill > 0.9 ? '#ff5050' : ACCENT }}>{ev.participants}/{ev.maxParticipants}</span>
                        </div>
                        <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${Math.min(fill * 100, 100)}%`, background: fill > 0.9 ? '#ff2020' : ACCENT, borderRadius: 2 }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {ev.tags.slice(0, 3).map(tag => (
                          <span key={tag} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 2, border: `1px solid ${ACCENT}33`, color: ACCENT + 'aa', letterSpacing: 1, textTransform: 'uppercase' }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                    <div style={{ height: 2, background: `linear-gradient(90deg,${ACCENT}00,${ACCENT},${ACCENT}00)` }} />
                  </div>
                );
              })}
            </div>
          )}
          <PageNav page={evPage} total={larpEvents.length} size={LARP_PAGE_SIZE} onChange={p => { setEvPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          <div style={{ marginTop: 20 }}><AdBox /></div>
        </div>
      </div>
    </div>
  );
}

// ── World Events Page ─────────────────────────────────────
const WORLD_TYPES = 'larp,rpg,ccg,fest';
const WORLD_LIMIT = 24;

const COUNTRY_FLAGS = {
  'Italy':'🇮🇹','Germany':'🇩🇪','Poland':'🇵🇱','France':'🇫🇷','United Kingdom':'🇬🇧','UK':'🇬🇧',
  'Spain':'🇪🇸','Czech Republic':'🇨🇿','Czechia':'🇨🇿','Slovakia':'🇸🇰','Hungary':'🇭🇺',
  'Austria':'🇦🇹','Switzerland':'🇨🇭','Netherlands':'🇳🇱','Belgium':'🇧🇪','Sweden':'🇸🇪',
  'Norway':'🇳🇴','Denmark':'🇩🇰','Finland':'🇫🇮','Russia':'🇷🇺','Ukraine':'🇺🇦',
  'USA':'🇺🇸','United States':'🇺🇸','Australia':'🇦🇺','Canada':'🇨🇦','Brazil':'🇧🇷',
  'Argentina':'🇦🇷','Mexico':'🇲🇽','Japan':'🇯🇵','Lithuania':'🇱🇹','Latvia':'🇱🇻',
  'Estonia':'🇪🇪','Portugal':'🇵🇹','Romania':'🇷🇴','Bulgaria':'🇧🇬','Croatia':'🇭🇷',
  'Serbia':'🇷🇸','Slovenia':'🇸🇮','Greece':'🇬🇷','Israel':'🇮🇱','South Africa':'🇿🇦',
  'New Zealand':'🇳🇿','Ireland':'🇮🇪','Scotland':'🏴󠁧󠁢󠁳󠁣󠁴󠁿',
};

const COUNTRY_DESCS = {
  'Italy': 'Italija – Europos LARP sostinė. Kasmet vyksta šimtai renginių, tarp kurių GnoccoCon, Terre di Confine ir daugybė regioninių festivalių.',
  'Germany': 'Vokietija – viena didžiausių LARP bendruomenių Europoje. ConQuest, Drachenfest ir Deutsche LARP Meisterschaften pritraukia tūkstančius žaidėjų.',
  'Poland': 'Lenkija – sparčiai auganti LARP scena su stipria fantastikos kultūra. Kaliskie Spotkania LARP ir didelis entuziastų būrys.',
  'United Kingdom': 'Jungtinė Karalystė – LARP tradicijų lopšys. Empire (Profound Decisions) ir Odyssey yra vieni didžiausių Europoje renginių.',
  'Sweden': 'Švedija – Nordic LARP judėjimo lyderė. Knutepunkt konferencija formuoja pasaulinę LARP teoriją ir praktiką.',
  'Czech Republic': 'Čekija – stipri LARP kultūra su tradiciniais ir naratyvistiniais žaidimais. Daug viduramžių stiliaus renginių.',
  'USA': 'JAV – įvairi LARP scena: boffer LARP, NERO, Amtgard ir daugybė skirtingų sistemų visoje šalyje.',
  'Austria': 'Austrija – aktyvi LARP scena, ypač viduramžių stilius. Daugybė festivalių Alpių regione.',
  'Hungary': 'Vengrija – augantis LARP judėjimas Vidurio Europoje su įvairiais žanrais.',
  'France': 'Prancūzija – aktyvus LARP judėjimas su stipriomis regioninėmis organizacijomis.',
  'Norway': 'Norvegija – Nordic LARP eksperimentinės tradicijos lopšys, artima bendruomenė.',
  'Denmark': 'Danija – Nordic LARP judėjimo dalis, daug inovatyvių žaidimų dizaino projektų.',
  'Australia': 'Australija – auganti LARP scena su skirtingomis sistemomis visame žemyne.',
  'Canada': 'Kanada – aktyvi LARP bendruomenė abiejuose pakrančių miestuose ir vidurio regionuose.',
  'Lithuania': 'Lietuva – auganti LARP scena su istoriniais ir fantastikos renginiais.',
  'Latvia': 'Latvija – aktyvi Baltijos LARP bendruomenė su daugybe renginių per metus.',
  'Estonia': 'Estija – stipri Baltijos LARP tradicija ir inovatyvūs žaidimų projektai.',
};

export function WorldEventsPage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [events, setEvents]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [selCountry, setSelCountry] = useState('');
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [q, setQ]                 = useState('');
  const [inputQ, setInputQ]       = useState('');
  const [sideClubs, setSideClubs]   = useState([]);
  const [sideNews, setSideNews]     = useState([]);
  const [sidePlayers, setSidePlayers] = useState([]);

  useEffect(() => {
    api.get('/events/countries', { params: { types: WORLD_TYPES } })
      .then(r => setCountries(r.data || []))
      .catch(() => {});
    api.get('/clubs').then(r => {
      const arr = r.data || [];
      setSideClubs([...arr].sort(() => Math.random() - 0.5).slice(0, 5));
    }).catch(() => {});
    api.get('/news', { params: { limit: 5, page: 1 } }).then(r => {
      const d = r.data;
      setSideNews(Array.isArray(d) ? d.slice(0, 5) : (d.news || d.items || []).slice(0, 5));
    }).catch(() => {});
    api.get('/players').then(r => {
      const arr = r.data || [];
      setSidePlayers([...arr].sort(() => Math.random() - 0.5).slice(0, 5));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { types: WORLD_TYPES, page, limit: WORLD_LIMIT };
    if (selCountry) params.country = selCountry;
    if (q) params.q = q;
    api.get('/events', { params })
      .then(r => {
        const d = r.data;
        if (Array.isArray(d)) { setEvents(d); setTotal(d.length); setPages(1); }
        else { setEvents(d.events || []); setTotal(d.total || 0); setPages(d.pages || 1); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selCountry, page, q]);

  const months = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];
  const desc = COUNTRY_DESCS[selCountry] || '';
  const flag = COUNTRY_FLAGS[selCountry] || '🌍';

  const handleSearch = (e) => { e.preventDefault(); setQ(inputQ.trim()); setPage(1); };

  return (
    <div style={{ background: '#050308', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#000d1a 0%,#041828 55%,#050308 100%)', padding: '56px 24px 44px', overflow: 'hidden', borderBottom: '1px solid rgba(100,180,255,0.15)' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(100,180,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(100,180,255,0.03) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse,rgba(100,180,255,0.08) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', border: '1px solid rgba(100,180,255,0.3)', borderRadius: 4, padding: '3px 14px', fontSize: 11, letterSpacing: 3, color: 'rgba(100,180,255,0.7)', textTransform: 'uppercase', marginBottom: 16 }}>✦ FANTASTAI.LT ✦</div>
          <div style={{ fontSize: 52, marginBottom: 10, filter: 'drop-shadow(0 0 24px rgba(100,180,255,0.5))' }}>🌍</div>
          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(24px,5vw,44px)', fontWeight: 700, color: '#64b4ff', margin: '0 0 12px', letterSpacing: 4, textTransform: 'uppercase', textShadow: '0 0 50px rgba(100,180,255,0.4)' }}>
            PASAULIO RENGINIAI
          </h1>
          <p style={{ color: 'rgba(160,210,255,0.55)', fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 20 }}>
            LARP · RPG · CCG · FESTIVALIS — VISAS PASAULIS
          </p>
          <div style={{ display: 'flex', gap: 18, justifyContent: 'center', color: 'rgba(160,210,255,0.4)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 24 }}>
            <span>🗺️ {countries.length} ŠALIŲ</span>
            <span>⚔️ {total} RENGINIŲ</span>
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '70%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(100,180,255,0.35),transparent)' }} />
      </div>

      {/* Main layout */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px', display: 'grid', gridTemplateColumns: '240px 1fr', gap: 28, alignItems: 'start' }}>

        {/* Sidebar */}
        <div style={{ position: 'sticky', top: 80 }}>
          {/* Search */}
          <form onSubmit={handleSearch} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={inputQ} onChange={e => setInputQ(e.target.value)}
                placeholder="Ieškoti..."
                style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(100,180,255,0.2)', borderRadius: 4, padding: '8px 12px', color: '#ede8f8', fontSize: 13 }}
              />
              <button type="submit" style={{ background: 'rgba(100,180,255,0.15)', border: '1px solid rgba(100,180,255,0.3)', borderRadius: 4, padding: '8px 12px', color: '#64b4ff', cursor: 'pointer', fontSize: 13 }}>🔍</button>
            </div>
          </form>

          {/* All countries button */}
          <button
            onClick={() => { setSelCountry(''); setPage(1); }}
            style={{ width: '100%', textAlign: 'left', background: !selCountry ? 'rgba(100,180,255,0.12)' : 'none', border: !selCountry ? '1px solid rgba(100,180,255,0.35)' : '1px solid transparent', borderRadius: 4, padding: '8px 12px', color: !selCountry ? '#64b4ff' : 'rgba(200,180,255,0.5)', cursor: 'pointer', fontSize: 13, fontWeight: !selCountry ? 700 : 400, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}
          >
            <span>🌍 Visos šalys</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>{countries.reduce((a, c) => a + c.count, 0)}</span>
          </button>

          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'rgba(100,180,255,0.5)', letterSpacing: 2, textTransform: 'uppercase', margin: '12px 0 8px', paddingBottom: 6, borderBottom: '1px solid rgba(100,180,255,0.1)' }}>Šalys</div>

          <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            {countries.map(c => (
              <button
                key={c.country}
                onClick={() => { setSelCountry(c.country); setPage(1); }}
                style={{ width: '100%', textAlign: 'left', background: selCountry === c.country ? 'rgba(100,180,255,0.12)' : 'none', border: selCountry === c.country ? '1px solid rgba(100,180,255,0.35)' : '1px solid transparent', borderRadius: 4, padding: '7px 12px', color: selCountry === c.country ? '#64b4ff' : 'rgba(200,180,255,0.6)', cursor: 'pointer', fontSize: 13, fontWeight: selCountry === c.country ? 700 : 400, marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>{COUNTRY_FLAGS[c.country] || '🌐'} {c.country}</span>
                <span style={{ fontSize: 11, opacity: 0.55, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '1px 6px' }}>{c.count}</span>
              </button>
            ))}
          </div>

          {/* ── 5 random clubs ── */}
          {sideClubs.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'rgba(100,180,255,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid rgba(100,180,255,0.1)' }}>🛡️ Klubai</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {sideClubs.map(c => (
                  <div key={c.id} onClick={() => navigate(`/clubs/${c.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(100,180,255,0.08)', cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.08)'}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{c.emoji || '🛡️'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(220,210,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      {c.city && <div style={{ fontSize: 10, color: 'rgba(150,150,180,0.6)', marginTop: 1 }}>📍 {c.city}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── last 5 news ── */}
          {sideNews.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'rgba(100,180,255,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid rgba(100,180,255,0.1)' }}>📰 Naujienos</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {sideNews.map(n => (
                  <div key={n.id} onClick={() => navigate(`/news/${n.id}`)}
                    style={{ padding: '6px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(100,180,255,0.08)', cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.08)'}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(220,210,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                    {n.created_at && <div style={{ fontSize: 10, color: 'rgba(150,150,180,0.6)', marginTop: 1 }}>{(n.created_at || '').slice(0, 10)}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── 5 random players ── */}
          {sidePlayers.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'rgba(100,180,255,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid rgba(100,180,255,0.1)' }}>👤 Žaidėjai</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {sidePlayers.map(p => (
                  <div key={p.user_id} onClick={() => navigate(`/players/${p.user_id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(100,180,255,0.08)', cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(100,180,255,0.08)'}
                  >
                    {p.avatar_url
                      ? <img src={p.avatar_url} alt={p.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(100,180,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>👤</span>
                    }
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(220,210,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || p.username}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Main content */}
        <div>
          {/* Country description */}
          {selCountry && (
            <div style={{ background: 'rgba(100,180,255,0.06)', border: '1px solid rgba(100,180,255,0.14)', borderRadius: 6, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 700, color: '#64b4ff', marginBottom: 6 }}>
                {flag} {selCountry}
              </div>
              {desc && <div style={{ color: 'rgba(200,180,255,0.7)', fontSize: 13, lineHeight: 1.6 }}>{desc}</div>}
            </div>
          )}

          {loading && <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(100,180,255,0.4)', fontFamily: 'Cinzel,serif', letterSpacing: 2 }}>KRAUNAMA...</div>}

          {!loading && events.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🌍</div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'rgba(100,180,255,0.4)', letterSpacing: 2 }}>RENGINIŲ NERASTA</div>
              <div style={{ color: 'rgba(200,180,255,0.4)', fontSize: 13, marginTop: 8 }}>Bandykite importuoti renginius iš larp-radar.com arba keiskite filtrą</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
            {events.map(ev => {
              const card = EV_CARD[ev.type] || EV_CARD.fest;
              const d = ev.date ? new Date(ev.date) : null;
              return (
                <div
                  key={ev.id}
                  onClick={() => navigate(`/events/${ev.id}`)}
                  style={{ cursor: 'pointer', background: '#0d0a18', border: `1px solid ${card.accent}18`, borderRadius: 6, overflow: 'hidden', transition: 'all .18s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = card.accent + '44'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = card.accent + '18'; }}
                >
                  {ev.image_url
                    ? <img src={ev.image_url} alt={ev.name} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                    : <div style={{ height: 100, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, opacity: 0.6 }}>{card.icon}</div>}
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: card.accent, textTransform: 'uppercase', fontFamily: 'Cinzel,serif' }}>{card.icon} {card.label}</span>
                      {d && <span style={{ fontSize: 11, color: 'rgba(200,180,255,0.5)' }}>{d.getDate()} {months[d.getMonth()]} {d.getFullYear()}</span>}
                    </div>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 700, color: '#ede8f8', marginBottom: 5, lineHeight: 1.3 }}>{ev.name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.45)' }}>
                      {COUNTRY_FLAGS[ev.country] || '🌐'} {ev.country}{ev.city ? `, ${ev.city}` : ''}
                    </div>
                  </div>
                  <div style={{ height: 2, background: `linear-gradient(90deg,${card.accent}00,${card.accent}88,${card.accent}00)` }} />
                </div>
              );
            })}
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 36 }}>
              <button onClick={() => { setPage(p => Math.max(1, p-1)); window.scrollTo({top:0,behavior:'smooth'}); }} disabled={page===1} style={{ background: 'rgba(100,180,255,0.08)', border: '1px solid rgba(100,180,255,0.2)', borderRadius: 4, padding: '7px 14px', color: '#64b4ff', cursor: page===1?'default':'pointer', opacity: page===1?.3:1 }}>←</button>
              <span style={{ color: 'rgba(200,180,255,0.5)', fontSize: 13 }}>{page} / {pages}</span>
              <button onClick={() => { setPage(p => Math.min(pages, p+1)); window.scrollTo({top:0,behavior:'smooth'}); }} disabled={page===pages} style={{ background: 'rgba(100,180,255,0.08)', border: '1px solid rgba(100,180,255,0.2)', borderRadius: 4, padding: '7px 14px', color: '#64b4ff', cursor: page===pages?'default':'pointer', opacity: page===pages?.3:1 }}>→</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
